import domtoimage from "dom-to-image";
import imageCompression from "browser-image-compression";
import {
  getStringDate,
  getStringDateAnalysis,
} from "../../Constants/CommonConstant";
import * as message from "./MessageConstant";

const PIXEL_TO_MM = 25.4 / 96;

export const getElementPdfDimensions = (elementId) => {
  const element = document.getElementById(elementId);
  if (!element) return { width: 0, height: 0 };

  // Use clientWidth/Height which represents the 100% CSS pixel size
  const cssWidth = element.clientWidth;
  const cssHeight = element.clientHeight;

  return {
    width: cssWidth * PIXEL_TO_MM,
    height: cssHeight * PIXEL_TO_MM,
  };
};

export const toPngReduceImage = async (elementId) => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id ${elementId} not found.`);
    return;
  }

  // Use a fixed high scale factor (e.g., 3) for consistent, high-resolution output.
  const SCALE_FACTOR = 3;

  // Get the element's dimensions *as if it were at 100% zoom* (CSS pixels).
  const width = element.clientWidth;
  const height = element.clientHeight;

  return await domtoimage.toPng(element, {
    quality: 1,
    bgcolor: "white",

    // Set the output PNG file dimensions to be high-res.
    width: width * SCALE_FACTOR,
    height: height * SCALE_FACTOR,

    // CRUCIAL: Scale the content visually for the high-res capture.
    style: {
      transform: `scale(${SCALE_FACTOR})`,
      transformOrigin: "top left",
      width: `${width}px`,
      height: `${height}px`,
      fontFamily: "Poppins",
      border: "none",
    },
  });
};

export const toPng = async (elementId) => {
  return await domtoimage.toPng(document.getElementById(elementId), {
    quality: 1, // Set maximum quality
    bgcolor: "white", // Ensure the background is white for better clarity
    width: elementId.offsetWidth * 2,
    height: elementId.offsetHeight * 2,
    style: {
      fontFamily: "Poppins",
      border: "none", // Optional: Remove border styling
    },
  });
};

export const compressImage = async (imageDataUrl) => {
  try {
    const image = await fetch(imageDataUrl);
    const blob = await image.blob();
    const compressedBlob = await imageCompression(blob, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
    });
    return URL.createObjectURL(compressedBlob);
  } catch (error) {
    console.error("Error compressing image:", error);
  }
};

export const loadImageAspectRatio = async (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = imageUrl;
  });
};

export const fetchFont = async (url) => {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();
  return btoa(
    new Uint8Array(buffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      ""
    )
  );
};

export const loadImage = async (src) => {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
};

export const isEmptyChart = async (obj) => {
  if (obj !== undefined && obj.length !== 0) {
    const data = obj[0];
    for (let key in data) {
      if (key !== "type" && parseFloat(data[key]) !== 0) {
        return true;
      }
    }
    return false;
  }
};

export const addPdfHeaderOld = async (doc, text, text2 = null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = text2 ? 17 : 12;
  const paddingTop = text2 ? 13 : 3;
  const paddingSide = text2 ? 0 : 3;
  let widthH = pageWidth - 2 * paddingSide;
  let heightH = headerHeight;

  const canvas = document.createElement("canvas");
  canvas.width = widthH;
  canvas.height = heightH;
  const ctx = canvas.getContext("2d");

  const gradient = ctx.createLinearGradient(0, 0, widthH, 0);
  gradient.addColorStop(0, "#8e0081"); // Purple color
  gradient.addColorStop(1, "#20008e"); // Dark blue color
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, widthH, heightH);

  const imgData = canvas.toDataURL("image/png");

  await doc.addImage(
    imgData,
    "PNG",
    paddingSide,
    paddingTop,
    widthH,
    headerHeight
  );

  doc.setTextColor(255, 255, 255); // White text color

  if (text2) {
    doc.setFont("Poppins", "medium");
    doc.setFontSize(19);
    doc.text(text, pageWidth / 2, 20, { align: "center" });
    doc.setFont("Poppins", "light");
    doc.setFontSize(16);
    doc.text(text2, pageWidth / 2, 28, {
      align: "center",
    });
  } else {
    doc.setFont("Poppins", "normal");
    doc.setFontSize(12);
    const textVerticalPosition =
      paddingTop + headerHeight / 2 + doc.getFontSize() / 8;
    doc.text(text, pageWidth / 2, textVerticalPosition, { align: "center" });
  }
};

export const addPdfHeader = async (doc, text, text2 = null, date = null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = date ? 25 : text2 ? 17 : 12;
  const paddingTop = text2 ? 13 : 3;
  const paddingSide = text2 ? 0 : 3;
  let widthH = pageWidth - 2 * paddingSide;
  let heightH = headerHeight;

  const canvas = document.createElement("canvas");
  canvas.width = widthH;
  canvas.height = heightH;
  const ctx = canvas.getContext("2d");

  const gradient = ctx.createLinearGradient(0, 0, widthH, 0);
  gradient.addColorStop(0, "#8e0081"); // Purple color
  gradient.addColorStop(1, "#20008e"); // Dark blue color
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, widthH, heightH);

  const imgData = canvas.toDataURL("image/png");

  await doc.addImage(
    imgData,
    "PNG",
    paddingSide,
    paddingTop,
    widthH,
    headerHeight
  );

  doc.setTextColor(255, 255, 255); // White text color

  if (text2) {
    doc.setFont("Poppins", "medium");
    doc.setFontSize(18);
    doc.text(text, pageWidth / 2, 20, { align: "center" });

    doc.setFont("Poppins", "light");
    doc.setFontSize(15);
    doc.text(text2, pageWidth / 2, 28, {
      align: "center",
    });

    if (date) {
      doc.setFont("Poppins", "light");
      doc.setFontSize(11);
      doc.text(date, pageWidth / 2, 35, {
        align: "center",
      });
    }
  } else {
    doc.setFont("Poppins", "normal");
    doc.setFontSize(12);
    const textVerticalPosition =
      paddingTop + headerHeight / 2 + doc.getFontSize() / 8;
    doc.text(text, pageWidth / 2, textVerticalPosition, { align: "center" });
  }
};

export const addImageToPDF = async (
  doc,
  imageData,
  x,
  y,
  width,
  height,
  newPage = false,
  header = false
) => {
  if (imageData) {
    if (newPage) {
      doc.addPage();
    }
    if (header === "top") {
      addPdfHeader(doc, `As of ${getStringDate(new Date())}`);
    } else if (header === "RIGHTSIZING") {
      addPdfHeader(doc, "RIGHTSIZING");
    } else if (header === "PARKING") {
      addPdfHeader(doc, "PARKING");
    } else if (header === "CLEANUP") {
      addPdfHeader(doc, "CLEANUP");
    }

    let widthDynamic = width;
    let heightDynamic = height;

    if (!width && !height) {
      const img = await loadImage(imageData);
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      widthDynamic = doc.internal.pageSize.getWidth() - 2 * x;
      heightDynamic = widthDynamic / aspectRatio;
    }

    doc.addImage(
      imageData,
      "PNG",
      x,
      y,
      widthDynamic,
      heightDynamic,
      undefined,
      "SLOW"
    );

    return heightDynamic + 10;
  }
};

export const textCenter = async (doc, textWidth) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const xCoordinate = (pageWidth - textWidth) / 2;
  return xCoordinate;
};

export const resourceChanged = async (resultData, entityType) => {
  return resultData.isResourceChanged !== undefined
    ? resultData.isResourceChanged === true
      ? (() => {
          switch (resultData.bvmState) {
            case "VALIDATED":
              return (
                message.VMResourceChngedFlagInfoTextSummaryValidated +
                "\n" +
                resultData.resourceChangeDetails
              );
              break;
            case "OPTIMIZING":
              switch (entityType) {
                case "vm":
                  return (
                    message.VMResourceCHangeFlagInfoTextSummaryOptimizing +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
                case "asp":
                  return (
                    message.ASPResourceCHangeFlagInfoTextSummaryOptimizing +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
                case "db":
                  return (
                    message.DBResourceCHangeFlagInfoTextSummaryOptimizing +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
              }
              break;
            case "OPTIMALLY SIZED":
              switch (entityType) {
                case "vm":
                  return (
                    message.VMResourceCHangeFlagInfoTextSummaryOptimized +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
                case "asp":
                  return (
                    message.ASPResourceCHangeFlagInfoTextSummaryOptimized +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
                case "db":
                  return (
                    message.DBResourceCHangeFlagInfoTextSummaryOptimized +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
              }
              break;
            default:
              switch (entityType) {
                case "vm":
                  return (
                    message.VMResourceChngedFlagInfoTextSummaryAnalyzed +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
                case "asp":
                  return (
                    message.ASPResourceChngedFlagInfoTextSummaryAnalyzed +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
                case "db":
                  return (
                    message.DBResourceChngedFlagInfoTextSummaryAnalyzed +
                    "\n" +
                    resultData.resourceChangeDetails
                  );
                  break;
              }
          }
        })()
      : null
    : null;
};

export const headerLogoAndTitle = async (doc, logo, companyName, title) => {
  const imageUrl = window.location.origin + "" + logo;
  const imageLogo = await loadImage(imageUrl);

  const aspectRatioLogo = imageLogo.naturalWidth / imageLogo.naturalHeight;
  let logoWidth = 16;
  let logoHeight = logoWidth / aspectRatioLogo;
  const pageWidth1 = doc.internal.pageSize.getWidth();
  const xCoordinate1 = (pageWidth1 - logoWidth) / 2;

  doc.addImage(imageLogo, "PNG", xCoordinate1, 1, logoWidth, logoHeight);
  await addPdfHeader(doc, companyName, title, getStringDate(new Date()));
};

export const headerLogoAnalysisDetails = async (
  doc,
  logo,
  companyName,
  title,
  platform
) => {
  const imageUrl = window.location.origin + "" + logo;
  const imageLogo = await loadImage(imageUrl);

  const aspectRatioLogo = imageLogo.naturalWidth / imageLogo.naturalHeight;
  let logoWidth = 16;
  let logoHeight = logoWidth / aspectRatioLogo;
  const pageWidth1 = doc.internal.pageSize.getWidth();
  const xCoordinate1 = (pageWidth1 - logoWidth) / 2;

  doc.addImage(imageLogo, "PNG", xCoordinate1, 1, logoWidth, logoHeight);
  await addPdfHeaderAnalysis(
    doc,
    companyName,
    title,
    platform,
    getStringDate(new Date())
  );
};

async function splitString(str, maxLength) {
  const words = str.split(" ");
  const result = [];
  let currentLine = "";

  for (let word of words) {
    if ((currentLine + word).length <= maxLength) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      result.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) {
    result.push(currentLine);
  }

  return result;
}

export const addPdfHeaderAnalysis = async (
  doc,
  companyName,
  title,
  platform = null,
  date = null
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  // const splitTexttitle = doc.splitTextToSize(title, 190);

  const splitTexttitle = await splitString(title, 70);

  let titleheight = 0;
  splitTexttitle.forEach((line, index) => {
    titleheight += index * 7;
  });

  const headerHeight = platform ? 39 + titleheight : 35 + titleheight;
  const paddingTop = 3;
  const paddingSide = 3;
  let widthH = pageWidth - 2 * paddingSide;
  let heightH = headerHeight;
  let topMargin = 15;

  const canvas = document.createElement("canvas");
  canvas.width = widthH;
  canvas.height = heightH + topMargin;
  const ctx = canvas.getContext("2d");

  const gradient = ctx.createLinearGradient(
    0,
    topMargin,
    widthH,
    heightH + topMargin
  );
  gradient.addColorStop(0, "#8e0081"); // Purple color
  gradient.addColorStop(1, "#20008e"); // Dark blue color
  ctx.fillStyle = gradient;
  ctx.fillRect(0, topMargin, widthH, heightH + topMargin);

  const imgData = canvas.toDataURL("image/png");

  await doc.addImage(
    imgData,
    "PNG",
    paddingSide,
    paddingTop,
    widthH,
    headerHeight
  );

  doc.setTextColor(255, 255, 255); // White text color

  doc.setFont("Poppins", "medium");
  doc.setFontSize(18);
  doc.text(companyName, pageWidth / 2, 20, { align: "center" });

  doc.setFont("Poppins", "light");
  doc.setFontSize(14);

  for (let index = 0; index < splitTexttitle.length; index++) {
    const line = splitTexttitle[index];
    const textWidth = doc.getTextWidth(line);
    const xCoordinateStr = (pageWidth - textWidth) / 2;
    doc.text(line, xCoordinateStr, 28 + index * 7);
  }

  // splitTexttitle.forEach((line, index) => {
  //   const textWidth = doc.getTextWidth(line);
  //   const xCoordinateStr = (pageWidth - textWidth) / 2;
  //   doc.text(line, xCoordinateStr, 28 + index * 7);
  // });

  if (platform) {
    doc.setFont("Poppins", "light");
    doc.setFontSize(9);
    doc.text(platform, pageWidth / 2, splitTexttitle.length > 1 ? 40 : 34, {
      align: "center",
    });
  }

  if (date) {
    doc.setFont("Poppins", "light");
    doc.setFontSize(11);
    doc.text(
      date,
      pageWidth / 2,
      platform
        ? splitTexttitle.length > 1
          ? 46
          : 40
        : splitTexttitle.length > 1
        ? 41
        : 35,
      {
        align: "center",
      }
    );
  }
};

export const VMInfoDetails = async (doc, startY, VMInfo, AnalyzedDate) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  doc.setFontSize(10);
  // doc.setFont("Poppins", "medium");
  doc.setTextColor(0, 0, 0);
  startY -= 1;
  const maxLineWidth = pageWidth - margin * 2;
  const splitTextVMInfo = doc.splitTextToSize(VMInfo, maxLineWidth);

  const lineHeight = 5;
  splitTextVMInfo.forEach((line, index) => {
    const textWidth = doc.getTextWidth(line);
    const xCoordinate = (pageWidth - textWidth) / 2;
    // startY += index * 5;
    doc.text(line, xCoordinate, startY + index * lineHeight);
  });
  startY += splitTextVMInfo.length * lineHeight;
  const textWidthDate = doc.getTextWidth(AnalyzedDate);
  // const xCoordinateStr = await textCenter(doc, doc.getTextWidth(AnalyzedDate));
  // startY += 5;
  const xCoordinateDate = (pageWidth - textWidthDate) / 2;

  doc.text(AnalyzedDate, xCoordinateDate, startY);
};

export const recommendationTable = async (
  doc,
  tblID,
  topMargin,
  fontStyle = true
) => {
  // const vCORE = await loadImageAsBase64(
  //   require("../../images/DB_vCore_02.png")
  // );
  // const iconCPU = await loadImageAsBase64(
  //   require("../../images/CPU_TestIcon_01.png")
  // );

  doc.autoTable({
    html: tblID,
    theme: "grid",
    margin: { top: topMargin, left: 4 },
    tableWidth: 202, // Fixed width
    styles: {
      font: "Poppins",
      fontSize: 8,
      cellPadding: 1,
      minCellHeight: 10,
      valign: "middle",
      halign: "center",
    },
    headStyles: {
      fillColor: [216, 216, 216], // Black background for headers
      textColor: [57, 57, 57],
      halign: "center",
      valign: "middle",
      fontSize: 9,
      fontStyle: "medium",
      lineWidth: 0.1, // Border width for header cells
    },
    bodyStyles: {
      fontStyle: "normal",
      textColor: [57, 57, 57],
      valign: "middle",
      halign: "center",
      fontSize: 8,
    },
    columnStyles: {
      0: { fontStyle: "medium", cellPadding: 2 }, // Set width for the first column
      1: { cellPadding: 2, cellWidth: 23, wordBreak: "break-word" }, // Set width for the second column
      2: { cellPadding: 2, cellWidth: 25, wordBreak: "break-word" }, // Set width for the third column
      3: { fontStyle: fontStyle ? "medium" : "", cellPadding: 2 },
      4: { cellPadding: 2 },
      5: { cellPadding: 2 },
      6: { cellPadding: 2 },
      7: { cellPadding: 2 },
      8: { cellPadding: 2 },
    },
    didParseCell: (data) => {
      if (data.section === "body") {
        // if (data.column.index === 0) {
        //   console.log("data", data.cell);

        //   // Draw the icon
        //   const icon = iconCPU;
        //   const cell = data.cell;
        //   console.log("cell", cell.y);
        //   // Add image inside the cell
        //   const imgX = cell.x + 2; // Left margin inside the cell
        //   const imgY = cell.y + topMargin + (cell.height - 8) / 2; // Center image vertically
        //   const imgWidth = 6; // Width of the image
        //   const imgHeight = 6; // Height of the image
        //   doc.addImage(icon, "PNG", imgX, imgY, imgWidth, imgHeight);

        //   console.log('imgY',imgY,cell.y,topMargin)

        //   // Add text inside the cell, next to the image
        //   const textX = imgX + imgWidth + 2; // Place text to the right of the image
        //   const textY = cell.y + topMargin; // Center text vertically
        //   doc.text("test", textX, textY, { baseline: "middle" });
        // }

        const cellHTML = data.cell.raw;
        const nestedTable = cellHTML.querySelector("table");

        if (nestedTable) {
          const extractedValues = [];
          // Loop through all rows in the table
          nestedTable.querySelectorAll("tr").forEach((row) => {
            // Get the first <td> in the row and extract its text content
            const cell = row.querySelector("td");
            if (cell) {
              extractedValues.push(cell.textContent.trim()); // Trim to remove extra spaces
            }
          });
          const updatedArray = extractedValues.flatMap((item, index) =>
            index < extractedValues.length - 1 ? [item, ""] : [item]
          );
          data.cell.text = updatedArray;
        }
      }
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15; // Reset startY to 15 for new pages
      }
    },
    showHead: "firstPage",
    head: false,
  });
};

export const recommendationReportTable = async (doc, tblID, topMargin) => {
  const startPage = doc.internal.getCurrentPageInfo().pageNumber;

  doc.autoTable({
    html: tblID,
    theme: "grid",
    margin: { top: topMargin, left: 4 },
    tableWidth: 202, // Fixed width
    styles: {
      font: "Poppins",
      fontSize: 8,
      cellPadding: 1,
      minCellHeight: 10,
      valign: "middle",
      halign: "center",
    },
    headStyles: {
      fillColor: [216, 216, 216], // Black background for headers
      textColor: [57, 57, 57],
      halign: "center",
      valign: "middle",
      fontSize: 9,
      fontStyle: "medium",
      lineWidth: 0.1, // Border width for header cells
    },
    bodyStyles: {
      fontStyle: "normal",
      textColor: [57, 57, 57],
      valign: "middle",
      halign: "center",
      fontSize: 8,
    },
    columnStyles: {
      0: { fontStyle: "medium", cellWidth: 23, cellPadding: 2 }, // Set width for the first column
      1: { cellWidth: 23, cellPadding: 2 }, // Set width for the second column
      2: { cellWidth: 25, cellPadding: 2 }, // Set width for the third column
      3: { cellWidth: 22, cellPadding: 2 },
      4: { cellWidth: 22, cellPadding: 2 },
      5: { cellWidth: 22, cellPadding: 2 },
      6: { cellWidth: 21, cellPadding: 2 },
      7: { cellWidth: 24, cellPadding: 2 },
      8: { cellWidth: 20, cellPadding: 2 },
    },
    didParseCell: (data) => {
      if (
        data.section === "head" &&
        data.row.index === 1 &&
        (data.column.index === 3 ||
          data.column.index === 4 ||
          data.column.index === 5)
      ) {
        data.cell.text = "Economical(P90)";
        if (data.column.index === 3) {
        } else if (data.column.index === 4) {
          data.cell.text = "Balanced (P95)";
        } else if (data.column.index === 5) {
          data.cell.text = "Enhanced (P99.5)";
        }
      }

      if (
        data.section === "body" &&
        data.row.raw[6] &&
        data.column.index === 6
      ) {
        const processedArray = data.cell.text.flatMap((item, index) => {
          if (index === 0 && item.includes("(")) {
            const match = item.match(/^(.*?)(\(.+\))$/);
            if (match) {
              return [match[1].trim(), match[2].trim()];
            }
          }
          return item;
        });
        data.cell.text = processedArray;
      }
      if (
        (data.section === "body" &&
          data.row.raw[3] &&
          data.column.index === 3) ||
        (data.row.raw[4] && data.column.index === 4) ||
        (data.row.raw[5] && data.column.index === 5)
      ) {
        const updatedArray = data.cell.text.flatMap((item, index) =>
          index < data.cell.text.length - 1 ? [item, ""] : [item]
        );
        data.cell.text = updatedArray;
      }
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15; // Reset startY to 15 for new pages
      }
    },
    showHead: "firstPage",
    head: false,
  });

  const endPage = doc.internal.getCurrentPageInfo().pageNumber;
  const pagesUsed = endPage - startPage + 1;
  return pagesUsed;
};

const addSvgToPDF = (doc, svgString, x, y, width, height) => {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });

    const url = URL.createObjectURL(svgBlob);

    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");

        // Original SVG size
        canvas.width = 690;
        canvas.height = 595;

        const ctx = canvas.getContext("2d");

        // White background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw SVG
        ctx.drawImage(img, 0, 0);

        // Convert SVG to PNG
        const pngData = canvas.toDataURL("image/png");

        // Add PNG to PDF
        doc.addImage(pngData, "PNG", x, y, width, height);

        URL.revokeObjectURL(url);

        resolve();
      } catch (error) {
        URL.revokeObjectURL(url);
        reject(error);
      }
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };

    img.src = url;
  });
};

export const analysisReport = async (
  doc,
  analysis,
  props,
  state,
  setPage = 0,
  analysisCnt,
  economicalPerfRatio,
  enhancedCostRatio,
  isOverall = false,
  isIndividual = false,
  pageReferencesForTableContentsIndividual,
  analysisTotolCnt,
  extraIndexPageCount,
  pdfGoalType = "",
  innerWidth = 768
) => {
  try {
    let isMobileDevice = innerWidth < 767;
    const pageTotalWidth = doc.internal.pageSize.getWidth();
    let vmName = analysis.name;
    let platform =
      analysis.metricsPlatform === "Host" ? " (Only Host Metrics Used)" : "";
    let VMInfo = analysis.vminfo;

    let AnalyzedDate = analysis.analysisDatetimestamp
      ? `Analyzed: ${getStringDateAnalysis(
          analysis.analysisDatetimestamp,
          props.userGmtId,
          props.userTimeZone
        )}`
      : null;

    AnalyzedDate +=
      analysis.bvmState === "ANALYZED" ||
      analysis.bvmState === "VERIFIED" ||
      analysis.bvmState === "OPTIMIZED"
        ? analysis.metricDataDurationUsed !== null
          ? `, ${analysis.metricDataDurationUsed}`
          : ""
        : "";

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageReferences = {};
    let pageReferencesForTableContent = {
      Summary: [],
      Economical: [],
      Balanced: [],
      Enhanced: [],
    };

    // start ["Aggressive", "Balanced", "Enhanced"]
    const analysisTypesArr = ["Economical", "Balanced", "Enhanced"];
    const loopAnalysis = pdfGoalType !== "" ? [pdfGoalType] : analysisTypesArr;
    for (const analysisType of loopAnalysis) {
      // Rightsizing Details

      if (
        (analysisType !== "Economical" || analysisCnt !== 1) &&
        pdfGoalType === ""
      ) {
        doc.addPage();
      }

      pageReferences[analysisType] =
        doc.internal.getCurrentPageInfo().pageNumber;

      pageReferencesForTableContent[analysisType].push({
        title: "Analysis",
        page: doc.internal.getCurrentPageInfo().pageNumber,
      });

      if (analysisType === "Economical" && isIndividual) {
        pageReferencesForTableContentsIndividual[vmName].push({
          title: "Analysis",
          page: doc.internal.getCurrentPageInfo().pageNumber,
        });
      }

      let startY = 10;
      const margin = 10;
      const maxLineWidth = pageWidth - margin * 2;
      const splitTexttitle = await splitString(
        "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
        70
      );
      let isResourceChanged = await resourceChanged(analysis, state.entityType);

      await headerLogoAnalysisDetails(
        doc,
        props.companyDetails.LOGO,
        props.companyDetails.CompanyName +
          " " +
          props.companyDetails.ServiceName,
        "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
        platform
      );

      if (platform) {
        startY += splitTexttitle.length > 1 ? 45 : 39;
      } else {
        startY += splitTexttitle.length > 1 ? 41 : 35;
      }

      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 10;

      if (isResourceChanged) {
        doc.setFontSize(9);
        doc.setFont("Poppins", "normal");
        doc.setTextColor(227, 34, 39);
        startY += 2;
        const resourceChangedLines = isResourceChanged.split("\n");
        for (let i = 0; i < resourceChangedLines.length; i++) {
          const line = resourceChangedLines[i];
          const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
          for (let j = 0; j < splitTextVMInfo.length; j++) {
            const textLine = splitTextVMInfo[j];
            const textWidth = doc.getTextWidth(textLine);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += 5;
            doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
          }
        }
      }

      if (analysis?.priceChangeMsg) {
        doc.setFontSize(9);
        doc.setFont("Poppins", "normal");
        doc.setTextColor(227, 34, 39);
        const splitTextPriceChangeMsg = doc.splitTextToSize(
          analysis.priceChangeMsg,
          maxLineWidth
        );
        startY += 5;
        splitTextPriceChangeMsg.forEach((line, index) => {
          const textWidth = doc.getTextWidth(line);
          const xCoordinate = (pageWidth - textWidth) / 2;
          startY += index * 5;
          doc.text(line, xCoordinate, startY);
        });
      }

      let pText = ``;
      let costReducingTxt = "";
      if (analysisType === "Balanced") {
        pText = `(P95)`;
        costReducingTxt = "Cost Reducing & Performance Improving";
      } else if (analysisType === "Enhanced") {
        pText = `(P99.5)`;
        costReducingTxt = "Performance Improving";
      } else {
        pText = `(P90)`;
        costReducingTxt = "Cost Reducing";
      }

      startY += 15;
      doc.setTextColor(57, 57, 57);
      doc.setFont("Poppins", "medium");
      let strEco = `${analysisType}${pText} Optimization Objectives`;
      doc.setFontSize(15);
      doc.text(strEco, (pageWidth - doc.getTextWidth(strEco)) / 2, startY);
      doc.setFont("Poppins", "light");
      doc.setFontSize(13);
      startY += 8;
      doc.text(
        costReducingTxt,
        (pageWidth - doc.getTextWidth(costReducingTxt)) / 2,
        startY
      );
      startY += 8;
      doc.setFont("Poppins", "medium");
      doc.setFontSize(13);
      doc.text(
        "Rightsizing Analysis",
        (pageWidth - doc.getTextWidth("Rightsizing Analysis")) / 2,
        startY
      );
      let costPerformanceImgDim = "";
      if (isMobileDevice) {
        costPerformanceImgDim = getElementPdfDimensions(
          "costPerformanceImg" + analysisType + analysisCnt
        );
      }

      startY += 10;
      let costPerformanceImg = await toPng(
        "costPerformanceImg" + analysisType + analysisCnt
      );
      let costPerformance = await compressImage(costPerformanceImg);

      let costPerformanceLeftPosition = "";
      if (costPerformanceImgDim) {
        costPerformanceLeftPosition =
          pageTotalWidth / 2 - (costPerformanceImgDim.width * 1.5) / 2;
      }

      await addImageToPDF(
        doc,
        costPerformance,
        costPerformanceLeftPosition,
        startY,
        costPerformanceImgDim ? costPerformanceImgDim.width * 1.5 : "",
        costPerformanceImgDim ? costPerformanceImgDim.height * 0.7 : "",
        ""
      );

      startY += 20;

      let costPerformanceTableDim = "";
      if (isMobileDevice) {
        costPerformanceTableDim = getElementPdfDimensions(
          "costPerformanceTable" + analysisType + analysisCnt
        );
      }
      let costPerformanceTableImg = await toPng(
        "costPerformanceTable" + analysisType + analysisCnt
      );
      let costPerformanceTable = await compressImage(costPerformanceTableImg);

      let costPerformanceTableLeftPosition = 10;
      if (costPerformanceTableDim) {
        costPerformanceTableLeftPosition =
          pageTotalWidth / 2 - (costPerformanceTableDim.width * 2) / 2;
      }

      await addImageToPDF(
        doc,
        costPerformanceTable,
        costPerformanceTableLeftPosition,
        startY,
        costPerformanceTableDim ? costPerformanceTableDim.width * 2 : "",
        costPerformanceTableDim ? costPerformanceTableDim.height * 0.8 : "",
        ""
      );

      // Economical

      doc.addPage();
      startY = 10;
      await headerLogoAnalysisDetails(
        doc,
        props.companyDetails.LOGO,
        props.companyDetails.CompanyName +
          " " +
          props.companyDetails.ServiceName,
        "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
        platform
      );

      if (platform) {
        startY += splitTexttitle.length > 1 ? 45 : 39;
      } else {
        startY += splitTexttitle.length > 1 ? 41 : 35;
      }
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 10;

      if (isResourceChanged) {
        doc.setFontSize(9);
        doc.setFont("Poppins", "normal");
        doc.setTextColor(227, 34, 39);
        startY += 2;
        const resourceChangedLines = isResourceChanged.split("\n");
        for (let i = 0; i < resourceChangedLines.length; i++) {
          const line = resourceChangedLines[i];
          const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
          for (let j = 0; j < splitTextVMInfo.length; j++) {
            const textLine = splitTextVMInfo[j];
            const textWidth = doc.getTextWidth(textLine);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += 5;
            doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
          }
        }
      }

      if (analysis?.priceChangeMsg) {
        doc.setFontSize(9);
        doc.setFont("Poppins", "normal");
        doc.setTextColor(227, 34, 39);
        const splitTextPriceChangeMsg = doc.splitTextToSize(
          analysis.priceChangeMsg,
          maxLineWidth
        );
        startY += 5;
        splitTextPriceChangeMsg.forEach((line, index) => {
          const textWidth = doc.getTextWidth(line);
          const xCoordinate = (pageWidth - textWidth) / 2;
          startY += index * 5;
          doc.text(line, xCoordinate, startY);
        });
      }
      startY += 15;
      doc.setTextColor(57, 57, 57);
      doc.setFont("Poppins", "medium");
      doc.setFontSize(13);
      doc.text(
        analysisType,
        (pageWidth - doc.getTextWidth(analysisType)) / 2,
        startY
      );
      startY += 10;

      const tbl = document.getElementById("tbl" + analysisType + analysisCnt);
      await recommendationTable(doc, tbl, startY, false);
      // Recommendation for Economical Optimization Objective

      let goalTypeArray = "";

      if (analysisType === "Balanced") {
        goalTypeArray = "balanced";
      } else if (analysisType === "Enhanced") {
        goalTypeArray = "conservative";
      } else {
        goalTypeArray = "aggressive";
      }

      for (const [index1, item] of analysis[goalTypeArray].vm.entries()) {
        let index = item.recommendationChoiceNum;
        doc.addPage();
        startY = 10;

        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );
        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);

        startY += 10;

        if (isResourceChanged) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          startY += 2;
          const resourceChangedLines = isResourceChanged.split("\n");
          for (let i = 0; i < resourceChangedLines.length; i++) {
            const line = resourceChangedLines[i];
            const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
            for (let j = 0; j < splitTextVMInfo.length; j++) {
              const textLine = splitTextVMInfo[j];
              const textWidth = doc.getTextWidth(textLine);
              const xCoordinate = (pageWidth - textWidth) / 2;
              startY += 5;
              doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
            }
          }
        }

        if (analysis?.priceChangeMsg) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          const splitTextPriceChangeMsg = doc.splitTextToSize(
            analysis.priceChangeMsg,
            maxLineWidth
          );
          startY += 5;
          splitTextPriceChangeMsg.forEach((line, index) => {
            const textWidth = doc.getTextWidth(line);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += index * 5;
            doc.text(line, xCoordinate, startY);
          });
        }

        const foundDisk =
          analysis[goalTypeArray]?.osdisk &&
          Array.isArray(analysis[goalTypeArray].osdisk)
            ? analysis[goalTypeArray].osdisk.find(
                (disk) => disk.recommendationChoiceNum === index
              )
            : null;

        var current_os_disk_name =
          foundDisk?.recommended_os_disk_name +
          "-" +
          foundDisk?.recommended_os_disk_memory;
        var current_os_disk_memory = foundDisk?.recommended_os_disk_memory;

        const dataDisk =
          Object.values(analysis[goalTypeArray]?.dataDisk || {})
            .flat()
            .filter((disk) => disk.recommendationChoiceNum === index)
            .map(
              (disk) =>
                `${disk.recommended_data_disk_name}-${disk.recommended_data_disk_memory}`
            )
            .join(", ") || "";

        let recTitle = "";
        if (index === 0) {
          recTitle = `Default Recommendation (${
            item.recommended_vmsize_name
          }, (${current_os_disk_name}${dataDisk ? `, ${dataDisk})` : ")"}) `;
        } else {
          recTitle = `Alternate ${index} Recommendation (${
            item.recommended_vmsize_name
          }, (${current_os_disk_name}${dataDisk ? `, ${dataDisk})` : ")"}) `;
        }

        let recTitleIndex = "";
        if (index === 0) {
          recTitleIndex = `Default Recommendation`;
        } else {
          recTitleIndex = `Alternate ${index} Recommendation`;
        }

        pageReferencesForTableContent[analysisType].push({
          title: recTitleIndex,
          page: doc.internal.getCurrentPageInfo().pageNumber,
        });

        if (analysisType === "Economical" && isIndividual) {
          pageReferencesForTableContentsIndividual[vmName].push({
            title: recTitleIndex,
            page: doc.internal.getCurrentPageInfo().pageNumber,
          });
        }

        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "Rightsizing Recommendation for " +
            analysisType +
            " Optimization Objective",
          (pageWidth -
            doc.getTextWidth(
              "Rightsizing Recommendation for " +
                analysisType +
                " Optimization Objective"
            )) /
            2,
          startY
        );
        startY += 10;

        await multipleRecommentionDetails(doc, startY, recTitle);

        // doc.text(
        //   recTitle,
        //   (pageWidth - doc.getTextWidth(recTitle)) / 2,
        //   startY
        // );

        startY += 10;

        let recOptimizationImg = await toPng(
          "optimizationAnalysisImg" + analysisType + analysisCnt + index
        );
        let recOptimization = await compressImage(recOptimizationImg);

        await addImageToPDF(doc, recOptimization, 10, startY, "", "", "");

        const recommendationObjectiveTbl = document.getElementById(
          "recommendationObjective" + analysisType + analysisCnt + index
        );
        await recommendationTable(doc, recommendationObjectiveTbl, startY + 20);

        // Expected Benefits & Drawbacks for Economical Optimization Objective

        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );
        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        // await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        // startY += 10;

        // if (isResourceChanged) {
        //   doc.setFontSize(9);
        //   doc.setFont("Poppins", "normal");
        //   doc.setTextColor(227, 34, 39);
        //   startY += 2;
        //   const resourceChangedLines = isResourceChanged.split("\n");
        //   for (let i = 0; i < resourceChangedLines.length; i++) {
        //     const line = resourceChangedLines[i];
        //     const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
        //     for (let j = 0; j < splitTextVMInfo.length; j++) {
        //       const textLine = splitTextVMInfo[j];
        //       const textWidth = doc.getTextWidth(textLine);
        //       const xCoordinate = (pageWidth - textWidth) / 2;
        //       startY += 5;
        //       doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
        //     }
        //   }
        // }

        // if (analysis?.priceChangeMsg) {
        //   doc.setFontSize(9);
        //   doc.setFont("Poppins", "normal");
        //   doc.setTextColor(227, 34, 39);
        //   const splitTextPriceChangeMsg = doc.splitTextToSize(
        //     analysis.priceChangeMsg,
        //     maxLineWidth
        //   );
        //   startY += 5;
        //   splitTextPriceChangeMsg.forEach((line, index) => {
        //     const textWidth = doc.getTextWidth(line);
        //     const xCoordinate = (pageWidth - textWidth) / 2;
        //     startY += index * 5;
        //     doc.text(line, xCoordinate, startY);
        //   });
        // }

        // pageReferencesForTableContent[analysisType].push({
        //   title: "AI Insight",
        //   page: doc.internal.getCurrentPageInfo().pageNumber,
        // });

        // if (analysisType === "Economical" && isIndividual) {
        //   pageReferencesForTableContentsIndividual[vmName].push({
        //     title: "AI Insight",
        //     page: doc.internal.getCurrentPageInfo().pageNumber,
        //   });
        // }

        startY += 5;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        // doc.text(
        //   "Expected Benefits & Drawbacks for " +
        //     analysisType +
        //     " Optimization Objective",
        //   (pageWidth -
        //     doc.getTextWidth(
        //       "Expected Benefits & Drawbacks for " +
        //         analysisType +
        //         " Optimization Objective"
        //     )) /
        //     2,
        //   startY
        // );
        // startY += 10;

        doc.text(
          " Benefits and Drawbacks",
          (pageWidth - doc.getTextWidth(" Benefits and Drawbacks")) / 2,
          startY
        );
        startY += 1;

        let expectedBenefitsDrawbacksImgDim = "";
        if (isMobileDevice) {
          expectedBenefitsDrawbacksImgDim = getElementPdfDimensions(
            "expectedBenefitsDrawbacksImg" + analysisType + analysisCnt + index
          );
        }

        let expectedBenefitsDrawbacksImg = await toPng(
          "expectedBenefitsDrawbacksImg" + analysisType + analysisCnt + index
        );
        let expectedBenefitsDrawbacks = await compressImage(
          expectedBenefitsDrawbacksImg
        );

        let expectedBenefitsDrawbacksLeftPosition = "";
        if (expectedBenefitsDrawbacksImgDim) {
          expectedBenefitsDrawbacksLeftPosition =
            pageTotalWidth / 2 -
            (expectedBenefitsDrawbacksImgDim.width * 2.5) / 2;
        }

        await addImageToPDF(
          doc,
          expectedBenefitsDrawbacks,
          expectedBenefitsDrawbacksLeftPosition,
          startY,
          expectedBenefitsDrawbacksImgDim
            ? expectedBenefitsDrawbacksImgDim.width * 2.5
            : "",
          expectedBenefitsDrawbacksImgDim
            ? expectedBenefitsDrawbacksImgDim.height * 1
            : "",
          ""
        );

        let expectedBenefitImageHeight = 10;

        let pageRes = await isPageBreakHeight(
          doc,
          startY + 5,
          expectedBenefitsDrawbacksImg
        );

        if (pageRes) {
          expectedBenefitImageHeight = pageRes.pageHeight / 2;
        }

        startY += expectedBenefitImageHeight + 5;

        let insightsandActionsImgDim = "";
        if (isMobileDevice) {
          insightsandActionsImgDim = getElementPdfDimensions(
            "insightsandActionsImg" + analysisType + analysisCnt + index
          );
        }

        let insightsandActionsImg = await toPng(
          "insightsandActionsImg" + analysisType + analysisCnt + index
        );
        let insightsandActions = await compressImage(insightsandActionsImg);

        let insightsandActionsLeftPosition = "";
        if (insightsandActionsImgDim) {
          insightsandActionsLeftPosition =
            pageTotalWidth / 2 - (insightsandActionsImgDim.width * 2.5) / 2;
        }

        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "Insights and Actions",
          (pageWidth - doc.getTextWidth("Insights and Actions")) / 2,
          startY
        );
        startY += 1;

        await addImageToPDF(
          doc,
          insightsandActions,
          insightsandActionsLeftPosition,
          startY,
          insightsandActionsImgDim ? insightsandActionsImgDim.width * 2.5 : "",
          insightsandActionsImgDim ? insightsandActionsImgDim.height * 1 : "",
          ""
        );

        if (item.rf_goal_plot !== undefined && item.rf_goal_plot !== null) {
          let insightImageHeight = 10;

          let pageResInsight = await isPageBreakHeight(
            doc,
            startY + 5,
            insightsandActionsImg
          );

          if (pageResInsight) {
            insightImageHeight = pageResInsight.pageHeight / 2.2;
          }

          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();

          // Height of the SVG (89) + title text & margins (~20) + bottom margin (~15)
          const plotHeight = 89;
          const totalPlotBlockHeight = plotHeight;
          const bottomMargin = 10;

          // Check if there is enough space remaining on the current page
          if (
            startY + insightImageHeight + totalPlotBlockHeight >
            pageHeight - bottomMargin
          ) {
            doc.addPage();
            startY = 10;

            await headerLogoAnalysisDetails(
              doc,
              props.companyDetails.LOGO,
              props.companyDetails.CompanyName +
                " " +
                props.companyDetails.ServiceName,
              "Rightsizing Details of " +
                vmName +
                " for " +
                analysisType +
                " Goal",
              platform
            );
            if (platform) {
              startY += splitTexttitle.length > 1 ? 45 : 39;
            } else {
              startY += splitTexttitle.length > 1 ? 41 : 35;
            }
            await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
            startY += 10;

            if (isResourceChanged) {
              doc.setFontSize(9);
              doc.setFont("Poppins", "normal");
              doc.setTextColor(227, 34, 39);
              startY += 2;
              const resourceChangedLines = isResourceChanged.split("\n");
              for (let i = 0; i < resourceChangedLines.length; i++) {
                const line = resourceChangedLines[i];
                const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
                for (let j = 0; j < splitTextVMInfo.length; j++) {
                  const textLine = splitTextVMInfo[j];
                  const textWidth = doc.getTextWidth(textLine);
                  const xCoordinate = (pageWidth - textWidth) / 2;
                  startY += 5;
                  doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
                }
              }
            }

            if (analysis?.priceChangeMsg) {
              doc.setFontSize(9);
              doc.setFont("Poppins", "normal");
              doc.setTextColor(227, 34, 39);
              const splitTextPriceChangeMsg = doc.splitTextToSize(
                analysis.priceChangeMsg,
                maxLineWidth
              );
              startY += 5;
              splitTextPriceChangeMsg.forEach((line, index) => {
                const textWidth = doc.getTextWidth(line);
                const xCoordinate = (pageWidth - textWidth) / 2;
                startY += index * 5;
                doc.text(line, xCoordinate, startY);
              });
            }
            startY += 7;
          } else {
            // Give adequate spacing from the previous section
            startY += insightImageHeight;
          }

          const x = (pageWidth - 120) / 2;

          doc.setTextColor(57, 57, 57);
          doc.setFont("Poppins", "medium");
          doc.setFontSize(13);
          doc.text(
            "Visual Insight",
            (pageWidth - doc.getTextWidth("Visual Insight")) / 2,
            startY
          );
          startY += 1;

          // 2. Draw SVG Plot centered
          const plotWidth = 120;
          await addSvgToPDF(
            doc,
            item.rf_goal_plot,
            x,
            startY,
            plotWidth,
            plotHeight
          );
        }
      }

      // GAI Recommendation for Economical Optimization Objective

      let gaiType =
        analysisType === "Economical"
          ? "Aggressive"
          : analysisType === "Enhanced"
          ? "Conservative"
          : analysisType;
      if (
        analysis &&
        analysis.gaiValidationData &&
        analysis.gaiValidationData.validationPerfType === gaiType
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );
        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);

        startY += 10;

        if (isResourceChanged) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          startY += 2;
          const resourceChangedLines = isResourceChanged.split("\n");
          for (let i = 0; i < resourceChangedLines.length; i++) {
            const line = resourceChangedLines[i];
            const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
            for (let j = 0; j < splitTextVMInfo.length; j++) {
              const textLine = splitTextVMInfo[j];
              const textWidth = doc.getTextWidth(textLine);
              const xCoordinate = (pageWidth - textWidth) / 2;
              startY += 5;
              doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
            }
          }
        }

        if (analysis?.priceChangeMsg) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          const splitTextPriceChangeMsg = doc.splitTextToSize(
            analysis.priceChangeMsg,
            maxLineWidth
          );
          startY += 5;
          splitTextPriceChangeMsg.forEach((line, index) => {
            const textWidth = doc.getTextWidth(line);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += index * 5;
            doc.text(line, xCoordinate, startY);
          });
        }

        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "GAI Recommendation for " + analysisType + " Optimization Objective",
          (pageWidth -
            doc.getTextWidth(
              "GAI Recommendation for " +
                analysisType +
                " Optimization Objective"
            )) /
            2,
          startY
        );
        startY += 10;
        let recOptimizationGAIImg = await toPng(
          "optimizationAnalysisGAIImg" + analysisType + analysisCnt
        );
        let recOptimizationGAI = await compressImage(recOptimizationGAIImg);
        await addImageToPDF(doc, recOptimizationGAI, "", startY, "", "", "");

        const recommendationObjectiveGAITbl = document.getElementById(
          "recommendationObjectiveGAI" + analysisType + analysisCnt
        );
        await recommendationTable(
          doc,
          recommendationObjectiveGAITbl,
          startY + 20
        );
      }

      // const expectedBenefitsDrawbacksTbl = document.getElementById(
      //   "expectedBenefitsDrawbacks" + analysisType + analysisCnt
      // );
      // await recommendationTable(doc, expectedBenefitsDrawbacksTbl, startY + 30);

      // GAI Expected Benefits & Drawbacks for Economical Optimization Objective
      if (
        analysis &&
        analysis.gaiValidationData &&
        analysis.gaiValidationData.validationPerfType === gaiType
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );
        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;

        if (isResourceChanged) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          startY += 2;
          const resourceChangedLines = isResourceChanged.split("\n");
          for (let i = 0; i < resourceChangedLines.length; i++) {
            const line = resourceChangedLines[i];
            const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
            for (let j = 0; j < splitTextVMInfo.length; j++) {
              const textLine = splitTextVMInfo[j];
              const textWidth = doc.getTextWidth(textLine);
              const xCoordinate = (pageWidth - textWidth) / 2;
              startY += 5;
              doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
            }
          }
        }

        if (analysis?.priceChangeMsg) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          const splitTextPriceChangeMsg = doc.splitTextToSize(
            analysis.priceChangeMsg,
            maxLineWidth
          );
          startY += 5;
          splitTextPriceChangeMsg.forEach((line, index) => {
            const textWidth = doc.getTextWidth(line);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += index * 5;
            doc.text(line, xCoordinate, startY);
          });
        }
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "GAI Expected Benefits & Drawbacks for " +
            analysisType +
            " Optimization Objective",
          (pageWidth -
            doc.getTextWidth(
              "GAI Expected Benefits & Drawbacks for " +
                analysisType +
                " Optimization Objective"
            )) /
            2,
          startY
        );
        startY += 10;
        let expectedBenefitsDrawbacksGAIImg = await toPng(
          "expectedBenefitsDrawbacksGAIImg" + analysisType + analysisCnt
        );
        let expectedBenefitsDrawbacksGAI = await compressImage(
          expectedBenefitsDrawbacksGAIImg
        );
        await addImageToPDF(
          doc,
          expectedBenefitsDrawbacksGAI,
          "",
          startY,
          "",
          "",
          ""
        );

        const expectedBenefitsDrawbacksGAITbl = document.getElementById(
          "expectedBenefitsDrawbacksGAI" + analysisType + analysisCnt
        );
        await recommendationTable(
          doc,
          expectedBenefitsDrawbacksGAITbl,
          startY + 30
        );
      }

      // Recommendation Validation Economical Optimization Objective

      let forValidation =
        analysis.validationData !== null &&
        analysis.validationData !== undefined;
      if (
        forValidation === true &&
        analysis.validationData.perfType === gaiType
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );
        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;

        if (isResourceChanged) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          startY += 2;
          const resourceChangedLines = isResourceChanged.split("\n");
          for (let i = 0; i < resourceChangedLines.length; i++) {
            const line = resourceChangedLines[i];
            const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
            for (let j = 0; j < splitTextVMInfo.length; j++) {
              const textLine = splitTextVMInfo[j];
              const textWidth = doc.getTextWidth(textLine);
              const xCoordinate = (pageWidth - textWidth) / 2;
              startY += 5;
              doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
            }
          }
        }

        if (analysis?.priceChangeMsg) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          const splitTextPriceChangeMsg = doc.splitTextToSize(
            analysis.priceChangeMsg,
            maxLineWidth
          );
          startY += 5;
          splitTextPriceChangeMsg.forEach((line, index) => {
            const textWidth = doc.getTextWidth(line);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += index * 5;
            doc.text(line, xCoordinate, startY);
          });
        }
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "Recommendation Validation for " +
            analysisType +
            " Optimization Objective",
          (pageWidth -
            doc.getTextWidth(
              "Recommendation Validation for " +
                analysisType +
                " Optimization Objective"
            )) /
            2,
          startY
        );
        startY += 10;

        let recommendationValidationObjectiveImgDim = "";
        if (isMobileDevice) {
          recommendationValidationObjectiveImgDim = getElementPdfDimensions(
            "recommendationValidationObjectiveImg" + analysisType + analysisCnt
          );
        }

        let recommendationValidationObjectiveImg = await toPng(
          "recommendationValidationObjectiveImg" + analysisType + analysisCnt
        );
        let recommendationValidationObjective = await compressImage(
          recommendationValidationObjectiveImg
        );

        let recommendationValidationObjectivePosition = "";
        if (recommendationValidationObjectiveImgDim) {
          recommendationValidationObjectivePosition =
            pageTotalWidth / 2 -
            (recommendationValidationObjectiveImgDim.width * 2.5) / 2;
        }

        await addImageToPDF(
          doc,
          recommendationValidationObjective,
          recommendationValidationObjectivePosition,
          startY,
          recommendationValidationObjectiveImgDim
            ? recommendationValidationObjectiveImgDim.width * 2.5
            : "",
          recommendationValidationObjectiveImgDim
            ? recommendationValidationObjectiveImgDim.height * 0.7
            : "",
          ""
        );

        const recommendationValidationObjectiveTbl = document.getElementById(
          "recommendationValidationObjective" + analysisType + analysisCnt
        );
        await recommendationTable(
          doc,
          recommendationValidationObjectiveTbl,
          startY + 30,
          false
        );
      }

      // Validated Benefits & Drawback Economical Optimization Objective
      if (
        forValidation === true &&
        analysis.validationData.perfType === gaiType &&
        analysis.validationData.validationVM !== undefined &&
        analysis.validationData.validationVM !== null
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );
        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;

        if (isResourceChanged) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          startY += 2;
          const resourceChangedLines = isResourceChanged.split("\n");
          for (let i = 0; i < resourceChangedLines.length; i++) {
            const line = resourceChangedLines[i];
            const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
            for (let j = 0; j < splitTextVMInfo.length; j++) {
              const textLine = splitTextVMInfo[j];
              const textWidth = doc.getTextWidth(textLine);
              const xCoordinate = (pageWidth - textWidth) / 2;
              startY += 5;
              doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
            }
          }
        }

        if (analysis?.priceChangeMsg) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          const splitTextPriceChangeMsg = doc.splitTextToSize(
            analysis.priceChangeMsg,
            maxLineWidth
          );
          startY += 5;
          splitTextPriceChangeMsg.forEach((line, index) => {
            const textWidth = doc.getTextWidth(line);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += index * 5;
            doc.text(line, xCoordinate, startY);
          });
        }
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "Recommendation Validation for " +
            analysisType +
            " Optimization Objective",
          (pageWidth -
            doc.getTextWidth(
              "Recommendation Validation for " +
                analysisType +
                " Optimization Objective"
            )) /
            2,
          startY
        );
        startY += 10;

        if (
          (analysis.aggressive.validation_summary &&
            analysisType === "Economical") ||
          (analysis.balanced.validation_summary &&
            analysisType === "Balanced") ||
          (analysis.conservative.validation_summary &&
            analysisType === "Enhanced")
        ) {
          let validatedExpectedBenefitsDrawbacksImg = await toPng(
            "validatedExpectedBenefitsDrawbacksImg" + analysisType + analysisCnt
          );

          let validatedExpectedBenefitsDrawbacks = await compressImage(
            validatedExpectedBenefitsDrawbacksImg
          );
          await addImageToPDF(
            doc,
            validatedExpectedBenefitsDrawbacks,
            "",
            startY,
            "",
            "",
            ""
          );
        }

        let validateImageHeight = 30;
        if (isMobileDevice) {
          let validatedExpectedBenefitsDrawbacksImg = await toPng(
            "validatedExpectedBenefitsDrawbacksImg" + analysisType + analysisCnt
          );

          let pageRes = await isPageBreakHeight(
            doc,
            startY + 5,
            validatedExpectedBenefitsDrawbacksImg
          );

          if (pageRes) {
            validateImageHeight = pageRes.pageHeight / 2.2;
          }
        }

        const validatedExpectedBenefitsDrawbacksTbl = document.getElementById(
          "validatedExpectedBenefitsDrawbacks" + analysisType + analysisCnt
        );
        await recommendationTable(
          doc,
          validatedExpectedBenefitsDrawbacksTbl,
          startY + validateImageHeight
        );
      }

      // Resizing of Recommendation Economical Optimization Objective
      if (
        (analysis.aggressive.isRecommendationSelected === true &&
          analysisType === "Economical") ||
        (analysis.balanced.isRecommendationSelected === true &&
          analysisType === "Balanced") ||
        (analysis.conservative.isRecommendationSelected === true &&
          analysisType === "Enhanced")
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );
        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;

        if (isResourceChanged) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          startY += 2;
          const resourceChangedLines = isResourceChanged.split("\n");
          for (let i = 0; i < resourceChangedLines.length; i++) {
            const line = resourceChangedLines[i];
            const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
            for (let j = 0; j < splitTextVMInfo.length; j++) {
              const textLine = splitTextVMInfo[j];
              const textWidth = doc.getTextWidth(textLine);
              const xCoordinate = (pageWidth - textWidth) / 2;
              startY += 5;
              doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
            }
          }
        }

        if (analysis?.priceChangeMsg) {
          doc.setFontSize(9);
          doc.setFont("Poppins", "normal");
          doc.setTextColor(227, 34, 39);
          const splitTextPriceChangeMsg = doc.splitTextToSize(
            analysis.priceChangeMsg,
            maxLineWidth
          );
          startY += 5;
          splitTextPriceChangeMsg.forEach((line, index) => {
            const textWidth = doc.getTextWidth(line);
            const xCoordinate = (pageWidth - textWidth) / 2;
            startY += index * 5;
            doc.text(line, xCoordinate, startY);
          });
        }
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "Resizing of Recommendation for " +
            analysisType +
            " Optimization Objective",
          (pageWidth -
            doc.getTextWidth(
              "Resizing of Recommendation for " +
                analysisType +
                " Optimization Objective"
            )) /
            2,
          startY
        );
        startY += 10;

        pageReferencesForTableContent[analysisType].push({
          title: "Resizing Status",
          page: doc.internal.getCurrentPageInfo().pageNumber,
        });

        if (analysisType === "Economical" && isIndividual) {
          pageReferencesForTableContentsIndividual[vmName].push({
            title: "Resizing Status",
            page: doc.internal.getCurrentPageInfo().pageNumber,
          });
        }

        let resizingRecommendationObjectiveImg = await toPng(
          "resizingRecommendationObjectiveImg" + analysisType + analysisCnt
        );
        let resizingRecommendationObjective = await compressImage(
          resizingRecommendationObjectiveImg
        );
        await addImageToPDF(
          doc,
          resizingRecommendationObjective,
          "",
          startY,
          "",
          "",
          ""
        );

        const resizingRecommendationObjectiveTbl = document.getElementById(
          "resizingRecommendationObjective" + analysisType + analysisCnt
        );
        await recommendationTable(
          doc,
          resizingRecommendationObjectiveTbl,
          startY + 30,
          false
        );
      }

      // start plot graphs

      let plotType =
        analysisType === "Economical"
          ? "aggressive"
          : analysisType === "Enhanced"
          ? "conservative"
          : "balanced";

      const isAllEmpty = Object.values(analysis[plotType]?.plotDTO || {}).every(
        (obj) => Object.values(obj).every((val) => val == null)
      );

      if (!isAllEmpty) {
        pageReferencesForTableContent[analysisType].push({
          title: "Analysis Plots",
          page: doc.internal.getCurrentPageInfo().pageNumber + 1,
        });
      }

      if (analysisType === "Economical" && isIndividual && !isAllEmpty) {
        pageReferencesForTableContentsIndividual[vmName].push({
          title: "Analysis Plots",
          page: doc.internal.getCurrentPageInfo().pageNumber + 1,
        });
      }

      if (
        analysis[plotType]?.plotDTO?.GPUPROC?.GPU_PERCENT_UTIL ||
        analysis[plotType]?.plotDTO?.GPUPROC?.GPU_UTILISATION_HEALTH_VALUE ||
        analysis[plotType]?.plotDTO?.GPUPROC?.GPU_SM_SATURATION_HEALTH_VALUE
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );

        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;
      }

      if (
        analysis[plotType]?.plotDTO?.GPUPROC?.GPU_PERCENT_UTIL ||
        analysis[plotType]?.plotDTO?.GPUPROC?.GPU_UTILISATION_HEALTH_VALUE
        // ||
        // analysis[plotType]?.plotDTO?.GPUPROC?.GPU_SM_SATURATION_HEALTH_VALUE
      ) {
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "GPU SM",
          (pageWidth - doc.getTextWidth("GPU SM")) / 2,
          startY
        );
        startY += 2;

        if (
          analysis[plotType]?.plotDTO?.GPUPROC?.GPU_UTILISATION_HEALTH_VALUE
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.GPUPROC
                ?.GPU_UTILISATION_HEALTH_VALUE,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        // if (
        //   analysis[plotType]?.plotDTO?.GPUPROC?.GPU_SM_SATURATION_HEALTH_VALUE
        // ) {
        //   await addImageToPDF(
        //     doc,
        //     "data:image/png;base64," +
        //       analysis[plotType]?.plotDTO?.GPUPROC
        //         ?.GPU_SM_SATURATION_HEALTH_VALUE,
        //     105,
        //     startY,
        //     100,
        //     90,
        //     ""
        //   );
        // }
        // startY += 105;

        if (analysis[plotType]?.plotDTO?.GPUPROC?.GPU_PERCENT_UTIL) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.GPUPROC?.GPU_PERCENT_UTIL,
            105,
            startY,
            100,
            90,
            ""
          );
        }
      }

      if (
        analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_PERCENT_UTIL ||
        analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_UTILISATION_HEALTH_VALUE ||
        analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_SATURATION_HEALTH_VALUE
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );

        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;
      }

      if (
        analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_PERCENT_UTIL ||
        analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_UTILISATION_HEALTH_VALUE ||
        analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_SATURATION_HEALTH_VALUE
      ) {
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "GPU Memory",
          (pageWidth - doc.getTextWidth("GPU Memory")) / 2,
          startY
        );
        startY += 2;

        if (
          analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_UTILISATION_HEALTH_VALUE
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.GPUMEM
                ?.GPU_MEM_UTILISATION_HEALTH_VALUE,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_SATURATION_HEALTH_VALUE
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.GPUMEM
                ?.GPU_MEM_SATURATION_HEALTH_VALUE,
            105,
            startY,
            100,
            90,
            ""
          );
        }
        startY += 105;

        if (analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_PERCENT_UTIL) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.GPUMEM?.GPU_MEM_PERCENT_UTIL,
            2,
            startY,
            100,
            90,
            ""
          );
        }
      }

      if (
        analysis[plotType]?.plotDTO?.CPU?.current_vcpu_percent_utilisation ||
        analysis[plotType]?.plotDTO?.CPU?.current_cpu_health_value ||
        analysis[plotType]?.plotDTO?.MEMORY
          ?.current_memory_percent_utilisation ||
        analysis[plotType]?.plotDTO?.MEMORY?.current_mem_health_value
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );

        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;
      }

      if (
        analysis[plotType]?.plotDTO?.CPU?.current_vcpu_percent_utilisation ||
        analysis[plotType]?.plotDTO?.CPU?.current_cpu_health_value
      ) {
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text("CPU", (pageWidth - doc.getTextWidth("CPU")) / 2, startY);
        startY += 2;
        if (
          analysis[plotType]?.plotDTO?.CPU?.current_vcpu_percent_utilisation
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.CPU
                ?.current_vcpu_percent_utilisation,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (analysis[plotType]?.plotDTO?.CPU?.current_cpu_health_value) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.CPU?.current_cpu_health_value,
            105,
            startY,
            100,
            90,
            ""
          );
        }
      }

      if (
        analysis[plotType]?.plotDTO?.MEMORY
          ?.current_memory_percent_utilisation ||
        analysis[plotType]?.plotDTO?.MEMORY?.current_mem_health_value
      ) {
        startY += 105;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "Memory",
          (pageWidth - doc.getTextWidth("Memory")) / 2,
          startY
        );
        startY += 2;

        if (
          analysis[plotType]?.plotDTO?.MEMORY
            ?.current_memory_percent_utilisation
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.MEMORY
                ?.current_memory_percent_utilisation,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (analysis[plotType]?.plotDTO?.MEMORY?.current_mem_health_value) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.MEMORY?.current_mem_health_value,
            105,
            startY,
            100,
            90,
            ""
          );
        }
      }

      if (
        analysis[plotType]?.plotDTO?.NETWORK
          ?.current_network_tx_bw_percent_utilisation ||
        analysis[plotType]?.plotDTO?.NETWORK
          ?.current_network_rx_bw_percent_utilisation ||
        analysis[plotType]?.plotDTO?.NETWORK?.NetRXPacketSize_health_value ||
        analysis[plotType]?.plotDTO?.NETWORK?.NetTXPacketSize_health_value
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );

        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;

        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "Network",
          (pageWidth - doc.getTextWidth("Network")) / 2,
          startY
        );
        startY += 2;
        if (
          analysis[plotType]?.plotDTO?.NETWORK
            ?.current_network_tx_bw_percent_utilisation
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.NETWORK
                ?.current_network_tx_bw_percent_utilisation,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.NETWORK
            ?.current_network_rx_bw_percent_utilisation
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.NETWORK
                ?.current_network_rx_bw_percent_utilisation,
            105,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.NETWORK?.NetRXPacketSize_health_value
        ) {
          startY += 105;
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.NETWORK
                ?.NetRXPacketSize_health_value,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.NETWORK?.NetTXPacketSize_health_value
        ) {
          if (
            !analysis[plotType]?.plotDTO?.NETWORK?.NetRXPacketSize_health_value
          ) {
            startY += 105;
          }

          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.NETWORK
                ?.NetTXPacketSize_health_value,
            105,
            startY,
            100,
            90,
            ""
          );
        }
      }

      if (
        state.entityType !== "db" &&
        (analysis[plotType]?.plotDTO?.OSDISK?.DiskReadIOPS_OS_PERCENT_UTIL ||
          analysis[plotType]?.plotDTO?.OSDISK?.DiskWriteIOPS_OS_PERCENT_UTIL ||
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskReadThroughput_OS_PERCENT_UTIL ||
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskWriteThroughput_OS_PERCENT_UTIL)
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );

        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;

        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "OS Disk",
          (pageWidth - doc.getTextWidth("OS Disk")) / 2,
          startY
        );
        startY += 2;
        if (analysis[plotType]?.plotDTO?.OSDISK?.DiskReadIOPS_OS_PERCENT_UTIL) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.OSDISK?.DiskReadIOPS_OS_PERCENT_UTIL,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.OSDISK?.DiskWriteIOPS_OS_PERCENT_UTIL
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.OSDISK
                ?.DiskWriteIOPS_OS_PERCENT_UTIL,
            105,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskReadThroughput_OS_PERCENT_UTIL
        ) {
          startY += 105;
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.OSDISK
                ?.DiskReadThroughput_OS_PERCENT_UTIL,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskWriteThroughput_OS_PERCENT_UTIL
        ) {
          if (
            !analysis[plotType]?.plotDTO?.OSDISK
              ?.DiskReadThroughput_OS_PERCENT_UTIL
          ) {
            startY += 105;
          }
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.OSDISK
                ?.DiskWriteThroughput_OS_PERCENT_UTIL,
            105,
            startY,
            100,
            90,
            ""
          );
        }
      }

      if (
        state.entityType !== "db" &&
        (analysis[plotType]?.plotDTO?.OSDISK?.DiskReadLatency_OS_health_value ||
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskWriteLatency_OS_health_value ||
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskAvgQueueLength_OS_health_value)
      ) {
        doc.addPage();
        startY = 10;
        await headerLogoAnalysisDetails(
          doc,
          props.companyDetails.LOGO,
          props.companyDetails.CompanyName +
            " " +
            props.companyDetails.ServiceName,
          "Rightsizing Details of " + vmName + " for " + analysisType + " Goal",
          platform
        );

        if (platform) {
          startY += splitTexttitle.length > 1 ? 45 : 39;
        } else {
          startY += splitTexttitle.length > 1 ? 41 : 35;
        }
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 10;
        startY += 15;
        doc.setTextColor(57, 57, 57);
        doc.setFont("Poppins", "medium");
        doc.setFontSize(13);
        doc.text(
          "OS Disk",
          (pageWidth - doc.getTextWidth("OS Disk")) / 2,
          startY
        );
        startY += 2;
        if (
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskReadLatency_OS_health_value &&
          !state.isAsp
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.OSDISK
                ?.DiskReadLatency_OS_health_value,
            2,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskWriteLatency_OS_health_value &&
          !state.isAsp
        ) {
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.OSDISK
                ?.DiskWriteLatency_OS_health_value,
            105,
            startY,
            100,
            90,
            ""
          );
        }

        if (
          analysis[plotType]?.plotDTO?.OSDISK
            ?.DiskAvgQueueLength_OS_health_value
        ) {
          startY += 105;
          await addImageToPDF(
            doc,
            "data:image/png;base64," +
              analysis[plotType]?.plotDTO?.OSDISK
                ?.DiskAvgQueueLength_OS_health_value,
            2,
            startY,
            100,
            90,
            ""
          );
        }
      }

      if (analysis[plotType]?.plotDTO?.DATADISK && state.entityType !== "db") {
        const datadiskArray = Object.values(
          analysis[plotType].plotDTO.DATADISK
        );
        let diskCnt = 0;
        for (const disk of datadiskArray) {
          if (
            disk.DiskReadIOPS_PERCENT_UTIL ||
            disk.DiskWriteIOPS_PERCENT_UTIL ||
            disk.DiskReadThroughput_PERCENT_UTIL ||
            disk.DiskWriteThroughput_PERCENT_UTIL
          ) {
            doc.addPage();
            startY = 10;
            await headerLogoAnalysisDetails(
              doc,
              props.companyDetails.LOGO,
              props.companyDetails.CompanyName +
                " " +
                props.companyDetails.ServiceName,
              "Rightsizing Details of " +
                vmName +
                " for " +
                analysisType +
                " Goal",
              platform
            );

            if (platform) {
              startY += splitTexttitle.length > 1 ? 45 : 39;
            } else {
              startY += splitTexttitle.length > 1 ? 41 : 35;
            }
            await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
            startY += 10;
            startY += 15;
            doc.setTextColor(57, 57, 57);
            doc.setFont("Poppins", "medium");
            doc.setFontSize(13);
            doc.text(
              "Data Disk " + diskCnt,
              (pageWidth - doc.getTextWidth("Data Disk " + diskCnt)) / 2,
              startY
            );
            startY += 2;
            if (disk.DiskReadIOPS_PERCENT_UTIL) {
              await addImageToPDF(
                doc,
                "data:image/png;base64," + disk.DiskReadIOPS_PERCENT_UTIL,
                2,
                startY,
                100,
                90,
                ""
              );
            }

            if (disk.DiskWriteIOPS_PERCENT_UTIL) {
              await addImageToPDF(
                doc,
                "data:image/png;base64," + disk.DiskWriteIOPS_PERCENT_UTIL,
                105,
                startY,
                100,
                90,
                ""
              );
            }

            if (disk.DiskReadThroughput_PERCENT_UTIL) {
              startY += 105;
              await addImageToPDF(
                doc,
                "data:image/png;base64," + disk.DiskReadThroughput_PERCENT_UTIL,
                2,
                startY,
                100,
                90,
                ""
              );
            }

            if (disk.DiskWriteThroughput_PERCENT_UTIL) {
              if (!disk.DiskReadThroughput_PERCENT_UTIL) {
                startY += 105;
              }
              await addImageToPDF(
                doc,
                "data:image/png;base64," +
                  disk.DiskWriteThroughput_PERCENT_UTIL,
                105,
                startY,
                100,
                90,
                ""
              );
            }
          }

          if (
            disk.DiskReadLatency_DT_health_value ||
            disk.DiskWriteLatency_DT_health_value ||
            disk.DiskAvgQueueLength_DT_health_value
          ) {
            doc.addPage();
            startY = 10;
            await headerLogoAnalysisDetails(
              doc,
              props.companyDetails.LOGO,
              props.companyDetails.CompanyName +
                " " +
                props.companyDetails.ServiceName,
              "Rightsizing Details of " +
                vmName +
                " for " +
                analysisType +
                " Goal",
              platform
            );

            if (platform) {
              startY += splitTexttitle.length > 1 ? 45 : 39;
            } else {
              startY += splitTexttitle.length > 1 ? 41 : 35;
            }
            await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
            startY += 10;
            startY += 15;
            doc.setTextColor(57, 57, 57);
            doc.setFont("Poppins", "medium");
            doc.setFontSize(13);
            doc.text(
              "Data Disk " + diskCnt,
              (pageWidth - doc.getTextWidth("Data Disk " + diskCnt)) / 2,
              startY
            );
            startY += 2;
            if (disk.DiskReadLatency_DT_health_value) {
              await addImageToPDF(
                doc,
                "data:image/png;base64," + disk.DiskReadLatency_DT_health_value,
                2,
                startY,
                100,
                90,
                ""
              );
            }

            if (disk.DiskWriteLatency_DT_health_value) {
              await addImageToPDF(
                doc,
                "data:image/png;base64," +
                  disk.DiskWriteLatency_DT_health_value,
                105,
                startY,
                100,
                90,
                ""
              );
            }

            if (disk.DiskAvgQueueLength_DT_health_value) {
              startY += 105;
              await addImageToPDF(
                doc,
                "data:image/png;base64," +
                  disk.DiskAvgQueueLength_DT_health_value,
                2,
                startY,
                100,
                90,
                ""
              );
            }
          }
          diskCnt += 1;
        }
      }
    }

    let end = doc.internal.getCurrentPageInfo().pageNumber;
    // end ["Aggressive", "Balanced", "Enhanced"]

    let endp = await anlysisSummary(
      doc,
      analysis,
      props,
      state,
      VMInfo,
      vmName,
      AnalyzedDate,
      pageReferences,
      setPage,
      analysisCnt,
      economicalPerfRatio,
      enhancedCostRatio,
      pageReferencesForTableContent,
      isOverall,
      isIndividual,
      pageReferencesForTableContentsIndividual,
      analysisTotolCnt,
      extraIndexPageCount,
      pdfGoalType,
      innerWidth
    );
    return end + endp;
  } catch (error) {
    console.error("Error in analysisReport:", error);
    throw error; // Rethrow to handle it in the parent function
  }
};

export const anlysisSummary = async (
  doc,
  analysis,
  props,
  state,
  VMInfo,
  vmName,
  AnalyzedDate,
  pageReferences,
  setPageNum,
  analysisCnt,
  economicalPerfRatio,
  enhancedCostRatio,
  pageReferencesForTableContent,
  isOverall,
  isIndividual,
  pageReferencesForTableContentsIndividual,
  analysisTotolCnt,
  extraIndexPageCount,
  pdfGoalType,
  innerWidth = 768
) => {
  let isMobileDevice = innerWidth < 767;
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const maxLineWidth = pageWidth - margin * 2;
  let height = 0;
  let isPageBreak = 0;
  let summaryCount = 1;
  let pageHeightY = 100;
  let isPageBreakCount = 0;
  let textheight = 0;

  const aggressive = Object.values(
    analysis["aggressive"]?.plotDTO?.GPUPROC
  ).every((value) => value === "");
  const conservative = Object.values(
    analysis["conservative"]?.plotDTO?.GPUPROC
  ).every((value) => value === "");
  const balanced = Object.values(analysis["balanced"]?.plotDTO?.GPUPROC).every(
    (value) => value === ""
  );

  const aggressiveGPUMEM = Object.values(
    analysis["aggressive"]?.plotDTO?.GPUMEM
  ).every((value) => value === "");
  const conservativeGPUMEM = Object.values(
    analysis["conservative"]?.plotDTO?.GPUMEM
  ).every((value) => value === "");
  const balancedGPUMEM = Object.values(
    analysis["balanced"]?.plotDTO?.GPUMEM
  ).every((value) => value === "");

  if (
    aggressive &&
    conservative &&
    balanced &&
    aggressiveGPUMEM &&
    conservativeGPUMEM &&
    balancedGPUMEM
  ) {
    ++isPageBreakCount;
  }

  if (
    analysis["aggressive"]?.gpuDTO?.some((item) => item.currentGpuNum > 0) ||
    analysis["conservative"]?.gpuDTO?.some((item) => item.currentGpuNum > 0) ||
    analysis["balanced"]?.gpuDTO?.some((item) => item.currentGpuNum > 0)
  ) {
    ++isPageBreakCount;
  }

  let isResourceChanged = await resourceChanged(analysis, state.entityType);
  if (isResourceChanged) {
    textheight += 2;
    const resourceChangedLines = isResourceChanged.split("\n");
    for (let i = 0; i < resourceChangedLines.length; i++) {
      const line = resourceChangedLines[i];
      const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
      for (let j = 0; j < splitTextVMInfo.length; j++) {
        textheight += 5;
      }
    }
    pageHeightY += textheight;
  }

  if (analysis?.priceChangeMsg) {
    const splitTextPriceChangeMsg = doc.splitTextToSize(
      analysis.priceChangeMsg,
      maxLineWidth
    );
    textheight += 5;
    splitTextPriceChangeMsg.forEach((line, index) => {
      textheight += index * 5;
    });
    pageHeightY += textheight;
  }

  let optimizationAnalysisImg = await toPng(
    "optimizationAnalysis" + analysisCnt
  );
  let optimizationAnalysis = await compressImage(optimizationAnalysisImg);

  let pageRes = await isPageBreakHeight(
    doc,
    pageHeightY + 5,
    optimizationAnalysis
  );
  if (pageRes.isBreak) {
    ++isPageBreakCount;
    pageHeightY = 65 + textheight;
  } else {
    pageHeightY += pageRes.pageHeight;
  }

  let optimizationRecommendationsImg = await toPng(
    "optimizationRecommendations" + analysisCnt
  );
  let optimizationRecommendations = await compressImage(
    optimizationRecommendationsImg
  );

  pageRes = await isPageBreakHeight(
    doc,
    pageHeightY + 5,
    optimizationRecommendations
  );

  if (pageRes.isBreak) {
    ++isPageBreakCount;
    pageHeightY = 65 + textheight;
  } else {
    pageHeightY += pageRes.pageHeight;
  }

  if (analysis.gaiValidationData) {
    let optimizationGAIRecommendationsImg = await toPng(
      "optimizationGAIRecommendations" + analysisCnt
    );
    let optimizationGAIRecommendations = await compressImage(
      optimizationGAIRecommendationsImg
    );
    pageRes = await isPageBreakHeight(
      doc,
      pageHeightY + 5,
      optimizationGAIRecommendations
    );
    if (pageRes.isBreak) {
      ++isPageBreakCount;
      pageHeightY = 65 + textheight;
    } else {
      pageHeightY += pageRes.pageHeight;
    }
  }

  // let recommended_ActionsImg = await toPng("recommended_Actions" + analysisCnt);
  // let recommended_Actions = await compressImage(recommended_ActionsImg);
  // pageRes = await isPageBreakHeight(doc, pageHeightY + 5, recommended_Actions);
  // if (pageRes.isBreak) {
  //   ++isPageBreakCount;
  //   pageHeightY = 65 + textheight;
  // } else {
  //   pageHeightY += pageRes.pageHeight;
  // }

  let expectedBenefits_DrawbacksOfRecommendationsImg = await toPng(
    "expectedBenefits_DrawbacksOfRecommendations" + analysisCnt
  );
  let expectedBenefits_DrawbacksOfRecommendations = await compressImage(
    expectedBenefits_DrawbacksOfRecommendationsImg
  );
  pageRes = await isPageBreakHeight(
    doc,
    pageHeightY + 5,
    expectedBenefits_DrawbacksOfRecommendations
  );
  if (pageRes.isBreak) {
    ++isPageBreakCount;
    pageHeightY = 65 + textheight;
  } else {
    pageHeightY += pageRes.pageHeight;
  }

  if (analysis.gaiValidationData) {
    let expectedBenefits_DrawbacksOfGAIRecommendationsImg = await toPng(
      "expectedBenefits_DrawbacksOfGAIRecommendations" + analysisCnt
    );
    let expectedBenefits_DrawbacksOfGAIRecommendations = await compressImage(
      expectedBenefits_DrawbacksOfGAIRecommendationsImg
    );
    pageRes = await isPageBreakHeight(
      doc,
      pageHeightY + 5,
      expectedBenefits_DrawbacksOfGAIRecommendations
    );
    if (pageRes.isBreak) {
      ++isPageBreakCount;
      pageHeightY = 65 + textheight;
    } else {
      pageHeightY += pageRes.pageHeight;
    }
  }

  if (
    analysis &&
    ((analysis.aggressive !== undefined &&
      analysis.aggressive.vm[0]?.gaiInsightSummary !== undefined &&
      analysis.aggressive.vm[0]?.gaiInsightSummary !== null) ||
      (analysis.balanced !== undefined &&
        analysis.balanced.vm[0]?.gaiInsightSummary !== undefined &&
        analysis.balanced.vm[0]?.gaiInsightSummary !== null) ||
      (analysis.conservative !== undefined &&
        analysis.conservative.vm[0]?.gaiInsightSummary !== undefined &&
        analysis.conservative.vm[0]?.gaiInsightSummary !== null))
  ) {
    let gaiInsightImg = await toPng("gaiInsight" + analysisCnt);
    let gaiInsight = await compressImage(gaiInsightImg);
    pageRes = await isPageBreakHeight(doc, pageHeightY + 5, gaiInsight);
    if (pageRes.isBreak) {
      ++isPageBreakCount;
      pageHeightY = 65 + textheight;
    } else {
      pageHeightY += pageRes.pageHeight;
    }
  }

  let isResizingDone =
    analysis.aggressive.isRecommendationSelected === true ||
    analysis.balanced.isRecommendationSelected === true ||
    analysis.conservative.isRecommendationSelected === true;

  if (isResizingDone) {
    let resizingRecommendationImg = await toPng(
      "resizingRecommendation" + analysisCnt
    );
    let resizingRecommendation = await compressImage(resizingRecommendationImg);
    pageRes = await isPageBreakHeight(
      doc,
      pageHeightY + 5,
      resizingRecommendation
    );
    if (pageRes.isBreak) {
      ++isPageBreakCount;
      pageHeightY = 65 + textheight;
    } else {
      pageHeightY += pageRes.pageHeight;
    }
  }

  if (
    analysis.validationData !== null &&
    analysis.validationData !== undefined
  ) {
    let recommendationValidationImg = await toPng(
      "recommendationValidation" + analysisCnt
    );
    let recommendationValidation = await compressImage(
      recommendationValidationImg
    );

    pageRes = await isPageBreakHeight(
      doc,
      pageHeightY + 5,
      recommendationValidation
    );
    if (pageRes.isBreak) {
      ++isPageBreakCount;
      pageHeightY = 65 + textheight;
    } else {
      pageHeightY += pageRes.pageHeight;
    }
  }

  if (
    analysis.validationData !== null &&
    analysis.validationData !== undefined &&
    analysis.validationData.validationVM !== null
  ) {
    let validatedBenefitsDrawbacksRecommendationImg = await toPng(
      "validatedBenefitsDrawbacksRecommendation" + analysisCnt
    );
    let validatedBenefitsDrawbacksRecommendation = await compressImage(
      validatedBenefitsDrawbacksRecommendationImg
    );
    pageRes = await isPageBreakHeight(
      doc,
      pageHeightY + 5,
      validatedBenefitsDrawbacksRecommendation
    );
    if (pageRes.isBreak) {
      ++isPageBreakCount;
      pageHeightY = 65 + textheight;
    } else {
      pageHeightY += pageRes.pageHeight;
    }
  }

  pageReferences["Economical"] =
    pageReferences["Economical"] + isPageBreakCount;
  pageReferences["Balanced"] =
    pageReferences["Balanced"] + isPageBreakCount + 1;
  pageReferences["Enhanced"] =
    pageReferences["Enhanced"] + isPageBreakCount + 1;

  if (isIndividual) {
    pageReferencesForTableContentsIndividual[analysis.name].unshift({
      title: "Summary",
      page: parseInt(setPageNum) + parseInt(extraIndexPageCount),
    });
  }
  if (!pdfGoalType) {
    doc.insertPage(setPageNum); // Inserts a new blank page at the top
    doc.setPage(setPageNum); // Switch to the newly added first page
    setPageNum++;
    summaryCount++;
    await headerLogoAndTitle(
      doc,
      props.companyDetails.LOGO,
      props.companyDetails.CompanyName + " " + props.companyDetails.ServiceName,
      "Rightsizing Summary of" + " " + vmName
    );

    let startY = 45;
    await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);

    startY += 10;

    if (isResourceChanged) {
      doc.setFontSize(8);
      doc.setFont("Poppins", "normal");
      doc.setTextColor(227, 34, 39);
      startY += 2;
      const resourceChangedLines = isResourceChanged.split("\n");
      for (let i = 0; i < resourceChangedLines.length; i++) {
        const line = resourceChangedLines[i];
        const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
        for (let j = 0; j < splitTextVMInfo.length; j++) {
          const textLine = splitTextVMInfo[j];
          const textWidth = doc.getTextWidth(textLine);
          const xCoordinate = (pageWidth - textWidth) / 2;
          startY += 5;
          doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
        }
      }
    }

    if (analysis?.priceChangeMsg) {
      const splitTextPriceChangeMsg = doc.splitTextToSize(
        analysis.priceChangeMsg,
        maxLineWidth
      );
      startY += 5;
      splitTextPriceChangeMsg.forEach((line, index) => {
        const textWidth = doc.getTextWidth(line);
        const xCoordinate = (pageWidth - textWidth) / 2;
        startY += index * 5;
        doc.text(line, xCoordinate, startY);
      });
    }

    startY += 5;
    await analysisTypeLink(
      doc,
      startY,
      pageReferences,
      economicalPerfRatio,
      enhancedCostRatio
    );
    startY += 43;

    // Rightsizing Analysis Image
    pageReferencesForTableContent["Summary"].push({
      title: "Analysis",
      page: summaryCount,
    });

    doc.setFont("Poppins", "normal");
    doc.setTextColor(57, 57, 57);
    let str = "Rightsizing Analysis";
    doc.setFontSize(12);
    const optimizationAnalysisStr = await textCenter(
      doc,
      doc.getTextWidth(str)
    );
    doc.setFont("Poppins", "medium");
    doc.text(str, optimizationAnalysisStr, startY);

    startY += 2;
    height = await addImageToPDF(
      doc,
      optimizationAnalysis,
      "",
      startY,
      "",
      "",
      ""
    );
    startY += height;

    isPageBreak = await checkPageBreak(
      doc,
      startY + 5,
      optimizationRecommendations,
      setPageNum
    );
    if (isPageBreak) {
      setPageNum++;
      summaryCount++;
      startY = 10;
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 15;
      await analysisTypeLink(
        doc,
        startY,
        pageReferences,
        economicalPerfRatio,
        enhancedCostRatio
      );
      startY += 43;
    }

    // Optimization Recommendations Image
    pageReferencesForTableContent["Summary"].push({
      title: "Recommendation",
      page: summaryCount,
    });

    doc.setFont("Poppins", "normal");
    doc.setTextColor(57, 57, 57);
    let strR = "Rightsizing Recommendations";
    doc.setFontSize(12);
    const optimizationRecommendationsStr = await textCenter(
      doc,
      doc.getTextWidth(strR)
    );
    doc.setFont("Poppins", "medium");
    doc.text(strR, optimizationRecommendationsStr, startY);

    startY += 3;
    height = await addImageToPDF(
      doc,
      optimizationRecommendations,
      "",
      startY,
      "",
      "",
      ""
    );
    startY += height;

    if (analysis.gaiValidationData) {
      // Optimization gai Recommendations Image

      let optimizationGAIRecommendationsImg = await toPng(
        "optimizationGAIRecommendations" + analysisCnt
      );
      let optimizationGAIRecommendations = await compressImage(
        optimizationGAIRecommendationsImg
      );

      let isPageBreak = await checkPageBreak(
        doc,
        startY + 5,
        optimizationGAIRecommendations,
        setPageNum
      );
      if (isPageBreak) {
        setPageNum++;
        summaryCount++;
        startY = 10;
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 15;
        await analysisTypeLink(
          doc,
          startY,
          pageReferences,
          economicalPerfRatio,
          enhancedCostRatio
        );
        startY += 43;
      }

      let strgaiR = "Optimization GAI Recommendations";
      doc.setTextColor(57, 57, 57);
      doc.setFontSize(12);
      const optimizationGaiRecommendationsStr = await textCenter(
        doc,
        doc.getTextWidth(strgaiR)
      );
      doc.setFont("Poppins", "medium");
      doc.text(strgaiR, optimizationGaiRecommendationsStr, startY);
      startY += 5;

      height = await addImageToPDF(
        doc,
        optimizationGAIRecommendations,
        "",
        startY,
        "",
        "",
        ""
      );
      startY += height;
    }

    // isPageBreak = await checkPageBreak(
    //   doc,
    //   startY + 5,
    //   recommended_Actions,
    //   setPageNum
    // );
    // if (isPageBreak) {
    //   setPageNum++;
    //   summaryCount++;
    //   startY = 10;
    //   await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
    //   startY += 15;
    //   await analysisTypeLink(
    //     doc,
    //     startY,
    //     pageReferences,
    //     economicalPerfRatio,
    //     enhancedCostRatio
    //   );
    //   startY += 43;
    // }

    // pageReferencesForTableContent["Summary"].push({
    //   title: "Recommendation Actions",
    //   page: summaryCount,
    // });

    // let strER = "Recommendation Actions";
    // doc.setTextColor(57, 57, 57);
    // doc.setFontSize(12);
    // const recommendedActionsStr = await textCenter(
    //   doc,
    //   doc.getTextWidth(strER)
    // );
    // doc.setFont("Poppins", "medium");
    // doc.text(strER, recommendedActionsStr, startY);

    // startY += 3;

    // height = await addImageToPDF(
    //   doc,
    //   recommended_Actions,
    //   "",
    //   startY,
    //   "",
    //   "",
    //   ""
    // );
    // startY += height;

    // Expected Benefits & Drawbacks of Recommendations

    isPageBreak = await checkPageBreak(
      doc,
      startY + 5,
      expectedBenefits_DrawbacksOfRecommendations,
      setPageNum
    );
    if (isPageBreak) {
      setPageNum++;
      summaryCount++;
      startY = 10;
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 15;
      await analysisTypeLink(
        doc,
        startY,
        pageReferences,
        economicalPerfRatio,
        enhancedCostRatio
      );
      startY += 43;
    }

    pageReferencesForTableContent["Summary"].push({
      title: "Benefits and Drawbacks",
      page: summaryCount,
    });

    let strE = "Benefits and Drawback";
    doc.setTextColor(57, 57, 57);
    doc.setFontSize(12);
    const expectedBenefits_DrawbacksOfRecommendationsStr = await textCenter(
      doc,
      doc.getTextWidth(strE)
    );
    doc.setFont("Poppins", "medium");
    doc.text(strE, expectedBenefits_DrawbacksOfRecommendationsStr, startY);

    startY += 3;

    height = await addImageToPDF(
      doc,
      expectedBenefits_DrawbacksOfRecommendations,
      "",
      startY,
      "",
      "",
      ""
    );
    startY += height;

    if (analysis.gaiValidationData) {
      // Expected Benefits & Drawbacks of Recommendations

      let expectedBenefits_DrawbacksOfGAIRecommendationsImg = await toPng(
        "expectedBenefits_DrawbacksOfGAIRecommendations" + analysisCnt
      );
      let expectedBenefits_DrawbacksOfGAIRecommendations = await compressImage(
        expectedBenefits_DrawbacksOfGAIRecommendationsImg
      );

      isPageBreak = await checkPageBreak(
        doc,
        startY,
        expectedBenefits_DrawbacksOfGAIRecommendations,
        setPageNum
      );
      if (isPageBreak) {
        setPageNum++;
        summaryCount++;
        startY = 5;
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 15;
        await analysisTypeLink(
          doc,
          startY,
          pageReferences,
          economicalPerfRatio,
          enhancedCostRatio
        );
        startY += 43;
      }

      let strE = "Expected Benefits & Drawbacks of GAI Recommendations";
      doc.setTextColor(57, 57, 57);
      doc.setFontSize(12);
      const expectedBenefits_DrawbacksOfGAIRecommendationsStr =
        await textCenter(doc, doc.getTextWidth(strE));
      doc.setFont("Poppins", "medium");
      doc.text(strE, expectedBenefits_DrawbacksOfGAIRecommendationsStr, startY);

      startY += 3;

      height = await addImageToPDF(
        doc,
        expectedBenefits_DrawbacksOfGAIRecommendations,
        "",
        startY,
        "",
        "",
        ""
      );
      startY += height;
    }

    // if (
    //   analysis &&
    //   ((analysis.aggressive !== undefined &&
    //     analysis.aggressive.vm[0]?.gaiInsightSummary !== undefined &&
    //     analysis.aggressive.vm[0]?.gaiInsightSummary !== null) ||
    //     (analysis.balanced !== undefined &&
    //       analysis.balanced.vm[0]?.gaiInsightSummary !== undefined &&
    //       analysis.balanced.vm[0]?.gaiInsightSummary !== null) ||
    //     (analysis.conservative !== undefined &&
    //       analysis.conservative.vm[0]?.gaiInsightSummary !== undefined &&
    //       analysis.conservative.vm[0]?.gaiInsightSummary !== null))
    // ) {
    //   // GAI Insight

    //   let gaiInsightImg = await toPng("gaiInsight" + analysisCnt);
    //   let gaiInsight = await compressImage(gaiInsightImg);

    //   isPageBreak = await checkPageBreak(
    //     doc,
    //     startY + 5,
    //     gaiInsight,
    //     setPageNum
    //   );
    //   if (isPageBreak) {
    //     setPageNum++;
    //     summaryCount++;
    //     startY = 10;
    //     await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
    //     startY += 15;
    //     await analysisTypeLink(
    //       doc,
    //       startY,
    //       pageReferences,
    //       economicalPerfRatio,
    //       enhancedCostRatio
    //     );
    //     startY += 43;
    //   }

    // pageReferencesForTableContent["Summary"].push({
    //   title: "AI Insights",
    //   page: summaryCount,
    // });

    // let strE = "AI Insights";
    // doc.setTextColor(57, 57, 57);
    // doc.setFontSize(12);
    // const gaiInsightStr = await textCenter(doc, doc.getTextWidth(strE));
    // doc.setFont("Poppins", "medium");
    // doc.text(strE, gaiInsightStr, startY);
    // startY += 3;
    // height = await addImageToPDF(doc, gaiInsight, "", startY, "", "", "");
    // startY += height;
    // }

    if (isResizingDone) {
      let resizingRecommendationImg = await toPng(
        "resizingRecommendation" + analysisCnt
      );
      let resizingRecommendation = await compressImage(
        resizingRecommendationImg
      );

      isPageBreak = await checkPageBreak(
        doc,
        startY + 10,
        resizingRecommendation,
        setPageNum
      );
      if (isPageBreak) {
        setPageNum++;
        summaryCount++;
        startY = 5;
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 15;
        await analysisTypeLink(
          doc,
          startY,
          pageReferences,
          economicalPerfRatio,
          enhancedCostRatio
        );
        startY += 43;
      }

      pageReferencesForTableContent["Summary"].push({
        title: "Resizing Status",
        page: summaryCount,
      });

      // Resizing of Recommendation
      let risizingRStr = "Resizing per Selected Recommendation";
      doc.setTextColor(57, 57, 57);
      doc.setFontSize(12);
      const resizingRecommendationStr = await textCenter(
        doc,
        doc.getTextWidth(risizingRStr)
      );
      doc.setFont("Poppins", "medium");
      doc.text(risizingRStr, resizingRecommendationStr, startY);
      startY += 3;
      height = await addImageToPDF(
        doc,
        resizingRecommendation,
        3,
        startY,
        "",
        "",
        ""
      );
      startY += height;
    }

    if (
      analysis.validationData !== null &&
      analysis.validationData !== undefined
    ) {
      // Recommendation Validation

      let recommendationValidationImgDim = "";
      if (isMobileDevice) {
        recommendationValidationImgDim = getElementPdfDimensions(
          "recommendationValidation" + analysisCnt
        );
      }

      let recommendationValidationImg = await toPng(
        "recommendationValidation" + analysisCnt
      );
      let recommendationValidation = await compressImage(
        recommendationValidationImg
      );

      isPageBreak = await checkPageBreak(
        doc,
        startY + 5,
        recommendationValidation,
        setPageNum
      );
      if (isPageBreak) {
        setPageNum++;
        summaryCount++;
        startY = 10;
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 15;
        await analysisTypeLink(
          doc,
          startY,
          pageReferences,
          economicalPerfRatio,
          enhancedCostRatio
        );
        startY += 43;
      }

      let strE = "Recommendation Validation";
      doc.setTextColor(57, 57, 57);
      doc.setFontSize(12);
      const recommendationValidationStr = await textCenter(
        doc,
        doc.getTextWidth(strE)
      );
      doc.setFont("Poppins", "medium");
      doc.text(strE, recommendationValidationStr, startY);

      startY += 3;

      // reduced size

      height = await addImageToPDF(
        doc,
        recommendationValidation,
        "",
        startY,
        recommendationValidationImgDim
          ? recommendationValidationImgDim.width * 1.3
          : "",
        recommendationValidationImgDim
          ? recommendationValidationImgDim.height * 0.9
          : "",
        ""
      );
      startY += height;
    }

    if (
      analysis.validationData !== null &&
      analysis.validationData !== undefined &&
      analysis.validationData.validationVM !== null
    ) {
      // Validated Benefits & Drawbacks of Recommendation

      let validatedBenefitsDrawbacksRecommendationImgDim = "";
      if (isMobileDevice) {
        validatedBenefitsDrawbacksRecommendationImgDim =
          getElementPdfDimensions(
            "validatedBenefitsDrawbacksRecommendation" + analysisCnt
          );
      }

      let validatedBenefitsDrawbacksRecommendationImg = await toPng(
        "validatedBenefitsDrawbacksRecommendation" + analysisCnt
      );
      let validatedBenefitsDrawbacksRecommendation = await compressImage(
        validatedBenefitsDrawbacksRecommendationImg
      );

      isPageBreak = await checkPageBreak(
        doc,
        startY + 5,
        validatedBenefitsDrawbacksRecommendation,
        setPageNum
      );
      if (isPageBreak) {
        setPageNum++;
        summaryCount++;
        startY = 10;
        await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
        startY += 15;
        await analysisTypeLink(
          doc,
          startY,
          pageReferences,
          economicalPerfRatio,
          enhancedCostRatio
        );
        startY += 43;
      }

      let strE = "Validated Benefits & Drawbacks of Recommendation";
      doc.setTextColor(57, 57, 57);
      doc.setFontSize(12);
      const validatedBenefitsDrawbacksRecommendationStr = await textCenter(
        doc,
        doc.getTextWidth(strE)
      );
      doc.setFont("Poppins", "medium");
      doc.text(strE, validatedBenefitsDrawbacksRecommendationStr, startY);

      startY += 3;

      height = await addImageToPDF(
        doc,
        validatedBenefitsDrawbacksRecommendation,
        "",
        startY,
        validatedBenefitsDrawbacksRecommendationImgDim
          ? validatedBenefitsDrawbacksRecommendationImgDim.width * 1.3
          : "",
        validatedBenefitsDrawbacksRecommendationImgDim
          ? validatedBenefitsDrawbacksRecommendationImgDim.height * 0.9
          : "",
        ""
      );
      startY += height;
    }
  }

  if (isIndividual) {
    var isOverallCount = isOverall
      ? summaryCount + extraIndexPageCount - 1
      : summaryCount + extraIndexPageCount - 1;

    if (analysisTotolCnt === analysisCnt) {
      let pageHeight = isOverall ? 260 : 250;

      let titlePageContentNo = 1;
      doc.insertPage(titlePageContentNo); // Inserts a new blank page at the top
      doc.setPage(titlePageContentNo); // Switch to the newly added first page
      // setPageNum++;
      // summaryCount++
      let y = 7;

      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(`Rightsizing Report for VMs`, 105, y, {
        align: "center",
      });
      y += 6;
      doc.setFontSize(10);
      doc.text(getStringDate(new Date()), 105, y, { align: "center" });
      y += 8;
      doc.setFontSize(12);
      doc.text(`Table of Contents`, 105, y, { align: "center" });
      y += 10;

      Object.entries(pageReferencesForTableContentsIndividual).forEach(
        ([analysisType, items]) => {
          if (y > pageHeight) {
            ++titlePageContentNo;
            doc.insertPage(titlePageContentNo); // Inserts a new blank page at the top
            doc.setPage(titlePageContentNo); // Switch to the newly added first page
            y = 15;
          }

          let isSummaryCount = analysisType !== "Report" ? isOverallCount : 0;

          let pageTitle = analysisType;
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(pageTitle, 10, y);
          y += 6;

          // Items under each section
          items.forEach((item) => {
            let isSummary = item.title !== "Summary" ? isSummaryCount : 0;

            doc.setFontSize(9.5);
            doc.setFont("helvetica", "normal");

            doc.text(item.title, 20, y);
            doc.text(String(item.page + isSummary), 200, y, {
              align: "right",
            });

            // Clickable link
            doc.link(20, y - 5, 180, 10, {
              pageNumber: item.page + isSummary,
            });

            y += 5.5;
          });
          y += 3;
        }
      );
    }
  } else {
    doc.insertPage(1); // Inserts a new blank page at the top
    doc.setPage(1); // Switch to the newly added first page
    // setPageNum++;
    // summaryCount++;

    let y = 10;

    // Title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(57, 57, 57);
    doc.text(
      `Rightsizing ${
        pdfGoalType ? pdfGoalType + " Goal " : ""
      }Report for VM ${vmName}`,
      105,
      y,
      {
        align: "center",
      }
    );
    y += 7;
    doc.setFontSize(10);
    doc.text(getStringDate(new Date()), 105, y, { align: "center" });
    y += 10;
    doc.setFontSize(12);
    doc.text(`Table of Contents`, 105, y, { align: "center" });
    y += 15;

    // Loop through each analysis type
    Object.entries(pageReferencesForTableContent).forEach(
      ([analysisType, items]) => {
        if (
          (pdfGoalType && analysisType === "Summary") ||
          (pdfGoalType && analysisType !== pdfGoalType)
        ) {
          return; // <-- skips this iteration
        }

        let isSummaryCount = analysisType !== "Summary" ? summaryCount : 0;

        let pageTitle =
          analysisType !== "Summary" ? analysisType + " Goal" : analysisType;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(pageTitle, 10, y);
        y += 6;

        items.forEach((item) => {
          doc.setFontSize(9.5);
          doc.setFont("helvetica", "normal");

          doc.text(item.title, 20, y);

          doc.text(String(item.page + isSummaryCount), 200, y, {
            align: "right",
          });

          doc.link(20, y - 5, 180, 10, {
            pageNumber: item.page + isSummaryCount,
          });

          y += 5.5;
          // if (y > 270) {
          //   doc.addPage();
          //   y = 30;
          // }
        });
        y += 3;
      }
    );
  }

  ++isPageBreakCount;

  return summaryCount;
};

export const anlysisSummaryEachVm = async (
  doc,
  analysis,
  props,
  state,
  setPageNum,
  analysisCnt,
  economicalPerfRatio,
  enhancedCostRatio,
  isOverall,
  isIndividual,
  pageReferencesForTableContentsIndividual = {},
  analysisTotolCnt,
  extraIndexPageCount,
  innerWidth = 768,
  pageReferences = {}
) => {
  let pageReferencesForTableContent = {
    Summary: [],
  };

  let vmName = analysis.name;
  let VMInfo = analysis.vminfo;

  let AnalyzedDate = analysis.analysisDatetimestamp
    ? `Analyzed: ${getStringDateAnalysis(
        analysis.analysisDatetimestamp,
        props.userGmtId,
        props.userTimeZone
      )}`
    : null;

  AnalyzedDate +=
    analysis.bvmState === "ANALYZED" ||
    analysis.bvmState === "VERIFIED" ||
    analysis.bvmState === "OPTIMIZED"
      ? analysis.metricDataDurationUsed !== null
        ? `, ${analysis.metricDataDurationUsed}`
        : ""
      : "";

  let isMobileDevice = innerWidth < 767;
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const maxLineWidth = pageWidth - margin * 2;
  let height = 0;
  let isPageBreak = 0;
  let summaryCount = 1;
  let isPageBreakCount = 0;

  let isResourceChanged = await resourceChanged(analysis, state.entityType);

  let optimizationAnalysisImg = await toPng(
    "optimizationAnalysis" + analysisCnt
  );
  let optimizationAnalysis = await compressImage(optimizationAnalysisImg);

  let optimizationRecommendationsImg = await toPng(
    "optimizationRecommendations" + analysisCnt
  );
  let optimizationRecommendations = await compressImage(
    optimizationRecommendationsImg
  );

  let expectedBenefits_DrawbacksOfRecommendationsImg = await toPng(
    "expectedBenefits_DrawbacksOfRecommendations" + analysisCnt
  );
  let expectedBenefits_DrawbacksOfRecommendations = await compressImage(
    expectedBenefits_DrawbacksOfRecommendationsImg
  );

  let isResizingDone =
    analysis.aggressive.isRecommendationSelected === true ||
    analysis.balanced.isRecommendationSelected === true ||
    analysis.conservative.isRecommendationSelected === true;

  pageReferences["Economical"] =
    pageReferences["Economical"] + isPageBreakCount;
  pageReferences["Balanced"] = pageReferences["Balanced"] + isPageBreakCount;
  pageReferences["Enhanced"] = pageReferences["Enhanced"] + isPageBreakCount;

  pageReferencesForTableContentsIndividual[vmName].push({
    title: "Summary",
    page: doc.internal.getCurrentPageInfo().pageNumber + 1,
  });

  await headerLogoAndTitle(
    doc,
    props.companyDetails.LOGO,
    props.companyDetails.CompanyName + " " + props.companyDetails.ServiceName,
    "Rightsizing Summary of" + " " + vmName
  );

  let startY = 45;
  await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);

  startY += 10;

  if (isResourceChanged) {
    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    doc.setTextColor(227, 34, 39);
    startY += 2;
    const resourceChangedLines = isResourceChanged.split("\n");
    for (let i = 0; i < resourceChangedLines.length; i++) {
      const line = resourceChangedLines[i];
      const splitTextVMInfo = doc.splitTextToSize(line, maxLineWidth);
      for (let j = 0; j < splitTextVMInfo.length; j++) {
        const textLine = splitTextVMInfo[j];
        const textWidth = doc.getTextWidth(textLine);
        const xCoordinate = (pageWidth - textWidth) / 2;
        startY += 5;
        doc.text(textLine, xCoordinate, startY); // Adjust the vertical spacing as needed
      }
    }
  }

  if (analysis?.priceChangeMsg) {
    const splitTextPriceChangeMsg = doc.splitTextToSize(
      analysis.priceChangeMsg,
      maxLineWidth
    );
    startY += 5;
    splitTextPriceChangeMsg.forEach((line, index) => {
      const textWidth = doc.getTextWidth(line);
      const xCoordinate = (pageWidth - textWidth) / 2;
      startY += index * 5;
      doc.text(line, xCoordinate, startY);
    });
  }

  startY += 5;
  await analysisTypeLink(
    doc,
    startY,
    pageReferences,
    economicalPerfRatio,
    enhancedCostRatio
  );
  startY += 43;

  // Rightsizing Analysis Image

  doc.setFont("Poppins", "normal");
  doc.setTextColor(57, 57, 57);
  let str = "Rightsizing Analysis";
  doc.setFontSize(12);
  const optimizationAnalysisStr = await textCenter(doc, doc.getTextWidth(str));
  doc.setFont("Poppins", "medium");
  doc.text(str, optimizationAnalysisStr, startY);

  startY += 2;
  height = await addImageToPDF(
    doc,
    optimizationAnalysis,
    "",
    startY,
    "",
    "",
    ""
  );
  startY += height;
  isPageBreak = await isPageBreakHeight(
    doc,
    startY + 5,
    optimizationRecommendations
  );
  if (isPageBreak.isBreak) {
    summaryCount++;
    doc.addPage();
    startY = 10;
    await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
    startY += 15;
    await analysisTypeLink(
      doc,
      startY,
      pageReferences,
      economicalPerfRatio,
      enhancedCostRatio
    );
    startY += 43;
  }

  // Optimization Recommendations Image

  doc.setFont("Poppins", "normal");
  doc.setTextColor(57, 57, 57);
  let strR = "Rightsizing Recommendations";
  doc.setFontSize(12);
  const optimizationRecommendationsStr = await textCenter(
    doc,
    doc.getTextWidth(strR)
  );
  doc.setFont("Poppins", "medium");
  doc.text(strR, optimizationRecommendationsStr, startY);

  startY += 3;
  height = await addImageToPDF(
    doc,
    optimizationRecommendations,
    "",
    startY,
    "",
    "",
    ""
  );
  startY += height;

  if (analysis.gaiValidationData) {
    // Optimization gai Recommendations Image

    let optimizationGAIRecommendationsImg = await toPng(
      "optimizationGAIRecommendations" + analysisCnt
    );
    let optimizationGAIRecommendations = await compressImage(
      optimizationGAIRecommendationsImg
    );

    let isPageBreak = await isPageBreakHeight(
      doc,
      startY + 5,
      optimizationGAIRecommendations
    );
    if (isPageBreak.isBreak) {
      summaryCount++;
      doc.addPage();
      startY = 10;
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 15;
      await analysisTypeLink(
        doc,
        startY,
        pageReferences,
        economicalPerfRatio,
        enhancedCostRatio
      );
      startY += 43;
    }

    let strgaiR = "Optimization GAI Recommendations";
    doc.setTextColor(57, 57, 57);
    doc.setFontSize(12);
    const optimizationGaiRecommendationsStr = await textCenter(
      doc,
      doc.getTextWidth(strgaiR)
    );
    doc.setFont("Poppins", "medium");
    doc.text(strgaiR, optimizationGaiRecommendationsStr, startY);
    startY += 5;

    height = await addImageToPDF(
      doc,
      optimizationGAIRecommendations,
      "",
      startY,
      "",
      "",
      ""
    );
    startY += height;
  }
  // Expected Benefits & Drawbacks of Recommendations

  isPageBreak = await isPageBreakHeight(
    doc,
    startY + 5,
    expectedBenefits_DrawbacksOfRecommendations
  );
  if (isPageBreak.isBreak) {
    summaryCount++;
    doc.addPage();
    startY = 10;
    await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
    startY += 15;
    await analysisTypeLink(
      doc,
      startY,
      pageReferences,
      economicalPerfRatio,
      enhancedCostRatio
    );
    startY += 43;
  }

  let strE = "Benefits and Drawback";
  doc.setTextColor(57, 57, 57);
  doc.setFontSize(12);
  const expectedBenefits_DrawbacksOfRecommendationsStr = await textCenter(
    doc,
    doc.getTextWidth(strE)
  );
  doc.setFont("Poppins", "medium");
  doc.text(strE, expectedBenefits_DrawbacksOfRecommendationsStr, startY);

  startY += 3;

  height = await addImageToPDF(
    doc,
    expectedBenefits_DrawbacksOfRecommendations,
    "",
    startY,
    "",
    "",
    ""
  );
  startY += height;

  if (analysis.gaiValidationData) {
    // Expected Benefits & Drawbacks of Recommendations

    let expectedBenefits_DrawbacksOfGAIRecommendationsImg = await toPng(
      "expectedBenefits_DrawbacksOfGAIRecommendations" + analysisCnt
    );
    let expectedBenefits_DrawbacksOfGAIRecommendations = await compressImage(
      expectedBenefits_DrawbacksOfGAIRecommendationsImg
    );

    isPageBreak = await isPageBreakHeight(
      doc,
      startY,
      expectedBenefits_DrawbacksOfGAIRecommendations
    );
    if (isPageBreak.isBreak) {
      summaryCount++;
      doc.addPage();
      startY = 5;
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 15;
      await analysisTypeLink(
        doc,
        startY,
        pageReferences,
        economicalPerfRatio,
        enhancedCostRatio
      );
      startY += 43;
    }

    let strE = "Expected Benefits & Drawbacks of GAI Recommendations";
    doc.setTextColor(57, 57, 57);
    doc.setFontSize(12);
    const expectedBenefits_DrawbacksOfGAIRecommendationsStr = await textCenter(
      doc,
      doc.getTextWidth(strE)
    );
    doc.setFont("Poppins", "medium");
    doc.text(strE, expectedBenefits_DrawbacksOfGAIRecommendationsStr, startY);

    startY += 3;

    height = await addImageToPDF(
      doc,
      expectedBenefits_DrawbacksOfGAIRecommendations,
      "",
      startY,
      "",
      "",
      ""
    );
    startY += height;
  }

  if (isResizingDone) {
    let resizingRecommendationImg = await toPng(
      "resizingRecommendation" + analysisCnt
    );
    let resizingRecommendation = await compressImage(resizingRecommendationImg);

    isPageBreak = await isPageBreakHeight(
      doc,
      startY + 10,
      resizingRecommendation
    );
    if (isPageBreak.isBreak) {
      summaryCount++;
      doc.addPage();
      startY = 5;
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 15;
      await analysisTypeLink(
        doc,
        startY,
        pageReferences,
        economicalPerfRatio,
        enhancedCostRatio
      );
      startY += 43;
    }

    pageReferencesForTableContent["Summary"].push({
      title: "Resizing Status",
      page: summaryCount,
    });

    // Resizing of Recommendation
    let risizingRStr = "Resizing per Selected Recommendation";
    doc.setTextColor(57, 57, 57);
    doc.setFontSize(12);
    const resizingRecommendationStr = await textCenter(
      doc,
      doc.getTextWidth(risizingRStr)
    );
    doc.setFont("Poppins", "medium");
    doc.text(risizingRStr, resizingRecommendationStr, startY);
    startY += 3;
    height = await addImageToPDF(
      doc,
      resizingRecommendation,
      3,
      startY,
      "",
      "",
      ""
    );
    startY += height;
  }

  if (
    analysis.validationData !== null &&
    analysis.validationData !== undefined
  ) {
    // Recommendation Validation

    let recommendationValidationImgDim = "";
    if (isMobileDevice) {
      recommendationValidationImgDim = getElementPdfDimensions(
        "recommendationValidation" + analysisCnt
      );
    }

    let recommendationValidationImg = await toPng(
      "recommendationValidation" + analysisCnt
    );
    let recommendationValidation = await compressImage(
      recommendationValidationImg
    );

    isPageBreak = await isPageBreakHeight(
      doc,
      startY + 5,
      recommendationValidation
    );
    if (isPageBreak.isBreak) {
      summaryCount++;
      doc.addPage();
      startY = 10;
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 15;
      await analysisTypeLink(
        doc,
        startY,
        pageReferences,
        economicalPerfRatio,
        enhancedCostRatio
      );
      startY += 43;
    }

    let strE = "Recommendation Validation";
    doc.setTextColor(57, 57, 57);
    doc.setFontSize(12);
    const recommendationValidationStr = await textCenter(
      doc,
      doc.getTextWidth(strE)
    );
    doc.setFont("Poppins", "medium");
    doc.text(strE, recommendationValidationStr, startY);

    startY += 3;

    // reduced size

    height = await addImageToPDF(
      doc,
      recommendationValidation,
      "",
      startY,
      recommendationValidationImgDim
        ? recommendationValidationImgDim.width * 1.3
        : "",
      recommendationValidationImgDim
        ? recommendationValidationImgDim.height * 0.9
        : "",
      ""
    );
    startY += height;
  }

  if (
    analysis.validationData !== null &&
    analysis.validationData !== undefined &&
    analysis.validationData.validationVM !== null
  ) {
    // Validated Benefits & Drawbacks of Recommendation

    let validatedBenefitsDrawbacksRecommendationImgDim = "";
    if (isMobileDevice) {
      validatedBenefitsDrawbacksRecommendationImgDim = getElementPdfDimensions(
        "validatedBenefitsDrawbacksRecommendation" + analysisCnt
      );
    }

    let validatedBenefitsDrawbacksRecommendationImg = await toPng(
      "validatedBenefitsDrawbacksRecommendation" + analysisCnt
    );
    let validatedBenefitsDrawbacksRecommendation = await compressImage(
      validatedBenefitsDrawbacksRecommendationImg
    );

    isPageBreak = await isPageBreakHeight(
      doc,
      startY + 5,
      validatedBenefitsDrawbacksRecommendation
    );
    if (isPageBreak.isBreak) {
      summaryCount++;
      doc.addPage();
      startY = 10;
      await VMInfoDetails(doc, startY, VMInfo, AnalyzedDate);
      startY += 15;
      await analysisTypeLink(
        doc,
        startY,
        pageReferences,
        economicalPerfRatio,
        enhancedCostRatio
      );
      startY += 43;
    }

    let strE = "Validated Benefits & Drawbacks of Recommendation";
    doc.setTextColor(57, 57, 57);
    doc.setFontSize(12);
    const validatedBenefitsDrawbacksRecommendationStr = await textCenter(
      doc,
      doc.getTextWidth(strE)
    );
    doc.setFont("Poppins", "medium");
    doc.text(strE, validatedBenefitsDrawbacksRecommendationStr, startY);

    startY += 3;

    height = await addImageToPDF(
      doc,
      validatedBenefitsDrawbacksRecommendation,
      "",
      startY,
      validatedBenefitsDrawbacksRecommendationImgDim
        ? validatedBenefitsDrawbacksRecommendationImgDim.width * 1.3
        : "",
      validatedBenefitsDrawbacksRecommendationImgDim
        ? validatedBenefitsDrawbacksRecommendationImgDim.height * 0.9
        : "",
      ""
    );
    startY += height;
  }
  // }

  var isOverallCount = isOverall
    ? summaryCount + extraIndexPageCount - 1
    : summaryCount + extraIndexPageCount - 1;

  if (analysisTotolCnt === analysisCnt) {
    let pageHeight = isOverall ? 260 : 250;

    let titlePageContentNo = 1;
    doc.insertPage(titlePageContentNo); // Inserts a new blank page at the top
    doc.setPage(titlePageContentNo); // Switch to the newly added first page
    let y = 7;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(`Rightsizing Report for VMs`, 105, y, {
      align: "center",
    });
    y += 6;
    doc.setFontSize(10);
    doc.text(getStringDate(new Date()), 105, y, { align: "center" });
    y += 8;
    doc.setFontSize(12);
    doc.text(`Table of Contents`, 105, y, { align: "center" });
    y += 10;

    Object.entries(pageReferencesForTableContentsIndividual).forEach(
      ([analysisType, items]) => {
        if (y > pageHeight) {
          ++titlePageContentNo;
          doc.insertPage(titlePageContentNo); // Inserts a new blank page at the top
          doc.setPage(titlePageContentNo); // Switch to the newly added first page
          y = 15;
        }

        let isSummaryCount = analysisType !== "Report" ? isOverallCount : 0;

        let pageTitle = analysisType;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(pageTitle, 10, y);
        y += 6;

        // Items under each section
        items.forEach((item) => {
          let isSummary = item.title !== "Summary" ? isSummaryCount : 0;

          doc.setFontSize(9.5);
          doc.setFont("helvetica", "normal");

          doc.text(item.title, 20, y);
          doc.text(String(item.page + isSummary), 200, y, {
            align: "right",
          });

          // Clickable link
          doc.link(20, y - 5, 180, 10, {
            pageNumber: item.page + isSummary,
          });

          y += 5.5;
        });
        y += 3;
      }
    );
  }

  ++isPageBreakCount;

  return doc.internal.getCurrentPageInfo().pageNumber;
};

export const analysisTypeLink = async (
  doc,
  startY,
  pageReferences,
  economicalPerfRatio,
  enhancedCostRatio
) => {
  const tableX = 3; // Starting X position
  let tableY = startY; // Starting Y position
  let cellWidth = 68; // Width of each cell
  let cellHeight = 24; // Height of each cell

  doc.setFont("Poppins", "normal");

  // Draw Table Borders
  doc.setDrawColor(201, 201, 201);
  doc.rect(tableX, tableY, cellWidth, cellHeight); // Economical cell
  doc.rect(tableX + cellWidth, tableY, cellWidth, cellHeight); // Balanced cell
  doc.rect(tableX + 2 * cellWidth, tableY, cellWidth, cellHeight); // Enhanced cell

  await centerText(
    doc,
    "Economical",
    "(P90)",
    tableX,
    tableY - 3,
    true,
    {
      pageNumber: pageReferences.Economical,
    },
    1
  );
  await centerText(doc, "Cost Reducing", "", tableX, tableY + 3, false);
  await centerText(
    doc,
    economicalPerfRatio === 0
      ? `Lowest Cost regardless of Performance`
      : `Lower Cost without going below ${economicalPerfRatio} x Current Performance`,
    "",
    tableX,
    tableY + 12,
    false,
    {},
    "ratio"
  );

  await centerText(
    doc,
    "Balanced",
    "(P95)",
    tableX + cellWidth,
    tableY - 3,
    true,
    {
      pageNumber: pageReferences.Balanced,
    },
    2
  );
  await centerText(
    doc,
    "Cost Reducing and Performance Improving",
    "",
    tableX + cellWidth,
    tableY + 5,
    false
  );
  await centerText(
    doc,
    "Lower Cost for Same Performance",
    "",
    tableX + cellWidth,
    tableY + 12,
    false,
    {},
    "ratio"
  );
  await centerText(
    doc,
    "Enhanced",
    "  (P99.5)",
    tableX + 2 * cellWidth,
    tableY - 3,
    true,
    {
      pageNumber: pageReferences.Enhanced,
    },
    3
  );
  await centerText(
    doc,
    "Performance Improving",
    "",
    tableX + 2 * cellWidth,
    tableY + 3,
    false
  );
  await centerText(
    doc,
    enhancedCostRatio === 999999
      ? `Highest Performance regardless of Cost`
      : `Better Performance without exceeding ${enhancedCostRatio} x Current Cost`,
    "",
    tableX + 2 * cellWidth,
    tableY + 12,
    false,
    {},
    "ratio"
  );

  tableY += 24;
  cellHeight = 13;
  cellWidth = 204;

  doc.setDrawColor(201, 201, 201);
  doc.rect(tableX, tableY, cellWidth, cellHeight);

  doc.setTextColor(57, 57, 57);
  doc.setFontSize(9);
  const xCoordinateStr = await textCenter(doc, doc.getTextWidth("Legend"));
  doc.setFont("Poppins", "medium");
  doc.text("Legend", xCoordinateStr, tableY + 4);

  // Coordinates and dimensions for the legend bar
  const barX = 75; // Starting X position
  const barY = tableY + 6; // Starting Y position
  const barHeight = 5; // Height of the bar
  const sectionWidth = 20; // Width of each section (Good, Fair, Poor)
  const radius = barHeight / 2;

  // Add Text
  doc.setFont("Poppins", "medium");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255); // White text for visibility

  // "Good" Section with Rounded Left Corner
  doc.setFillColor("#007E60"); // Green
  doc.circle(barX + radius, barY + radius, radius, "F"); // Circle (filled)
  doc.rect(barX + radius, barY, sectionWidth - radius, barHeight, "F"); // Regular rectangle
  doc.text("Good", barX + 7, barY + 3.5);

  // "Fair" Section (Middle - No Rounded Corners)
  doc.setFillColor("#FFB939"); // Yellow
  doc.rect(barX + sectionWidth, barY, sectionWidth, barHeight, "F"); // Regular rectangle
  doc.text("Fair", barX + sectionWidth + 7, barY + 3.5);

  // "Poor" Section with Rounded Right Corner
  doc.setFillColor("#C30000"); // Red
  doc.setDrawColor(0, 255, 0);
  doc.rect(
    barX + 2 * sectionWidth,
    barY,
    sectionWidth - radius,
    barHeight,
    "F"
  ); // Regular rectangle
  doc.circle(
    barX + 2 * sectionWidth + sectionWidth - radius,
    barY + radius,
    radius,
    "F"
  ); // Circle (filled)
  doc.text("Poor", barX + 2 * sectionWidth + 7, barY + 3.5);
};

export const centerText = async (
  doc,
  text,
  text2,
  cellX,
  cellY,
  isLink = false,
  linkOptions = {},
  cnt
) => {
  const cellWidth = 68; // Width of each cell
  const cellHeight = 22; // Height of each cell

  const centerX = cellX + cellWidth / 2;
  const centerY = cellY + cellHeight / 2 - 2; // Adjusted for better vertical centering
  if (isLink) {
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 255); // Blue for links

    doc.textWithLink(text, centerX - 5, centerY, {
      align: "center",
      ...linkOptions,
    });
    doc.setFontSize(10);
    doc.textWithLink(
      text2,
      cnt === 1 ? centerX + 14.5 : cnt === 2 ? centerX + 12 : centerX + 13,
      centerY,
      {
        align: "center",
        ...linkOptions,
      }
    );
    doc.setTextColor(0, 0, 0); // Reset color
  } else {
    doc.setTextColor(0, 0, 0);
    if (cnt === "ratio") {
      doc.setFontSize(8);
    } else {
      doc.setFontSize(10);
    }

    // Handle multi-line text
    const lines = doc.splitTextToSize(text, cellWidth - 10); // 5 padding on each side
    const lineHeight = 4; // Approximate line height
    const totalTextHeight = lines.length * lineHeight;
    let currentY = cellY + (cellHeight - totalTextHeight) / 2;

    lines.forEach((line) => {
      doc.text(line, centerX, currentY, { align: "center" });
      currentY += lineHeight;
    });
  }
};

// Helper function to handle page break
export const checkPageBreak = async (doc, yPosition, imageData, page) => {
  let contentHeight = await getImageHeight(doc, imageData);
  const PAGE_MARGIN = 10; // Top and bottom margin
  const PAGE_HEIGHT = doc.internal.pageSize.height;
  if (yPosition + contentHeight > PAGE_HEIGHT - PAGE_MARGIN) {
    doc.insertPage(page);
    doc.setPage(page);
    return true; // Reset to top margin
  }
  return false;
};

// Helper function to handle page break
export const isPageBreakHeight = async (doc, yPosition, imageData) => {
  let contentHeight = await getImageHeight(doc, imageData);
  const PAGE_MARGIN = 10; // Top and bottom margin
  const PAGE_HEIGHT = doc.internal.pageSize.height;
  if (yPosition + contentHeight > PAGE_HEIGHT - PAGE_MARGIN) {
    return { isBreak: true, height: yPosition + contentHeight }; // Reset to top margin
  }
  return { isBreak: false, pageHeight: yPosition + contentHeight };
};

async function getImageHeight(doc, imageData) {
  const img = await loadImage(imageData);
  const aspectRatio = img.naturalWidth / img.naturalHeight;
  let widthDynamic = doc.internal.pageSize.getWidth(); // Adjust width as needed
  return (await widthDynamic) / aspectRatio;
}

const loadImageAsBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const multipleRecommentionDetails = async (doc, startY, VMInfo) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  doc.setFontSize(10);
  doc.setFont("Poppins", "medium");
  doc.setTextColor(0, 0, 0);
  startY -= 1;
  const maxLineWidth = pageWidth - margin * 2;
  const splitTextVMInfo = doc.splitTextToSize(VMInfo, maxLineWidth);

  const lineHeight = 5;
  splitTextVMInfo.forEach((line, index) => {
    const textWidth = doc.getTextWidth(line);
    const xCoordinate = (pageWidth - textWidth) / 2;
    // startY += index * 5;
    doc.text(line, xCoordinate, startY + index * lineHeight);
  });
  startY += splitTextVMInfo.length * lineHeight;
};
